// Planner v2 — pick real spots + season + days -> a realistic, proximity-clustered plan (~6h/day)
import { LUNCH_VENUES, DINNER_VENUES, LUNCH_COVER, DINNER_COVER, nearestVenues } from "./meals";

// Months drive availability now (1 = Jan … 12 = Dec)
// The suggested 3-day itinerary (see /3-days) — day by day, in its own walking
// order. Shared by the itinerary page link and the planner's auto-fill button.
// Day 1 walks north→south: Kastellet → Nyboder → the royals → Nyhavn → canal
// boat → Broens. Day 2: the old town, down Strøget, ending at Tivoli. Day 3: Christianshavn.
export const ITINERARY_3D = [
  ["littlemermaid", "nyboder", "marmorkirken", "amalienborg", "nyhavn", "canaltour", "broens"],
  ["torvehallerne", "rundetaarn", "graabroedretorv", "latinquarter", "sanktpeders", "stroget", "tivoli"],
  ["canalstroll", "sofiegade", "amagergade", "oursaviour", "christiania", "reffen", "opera"],
];

// Days 4 & 5 for longer stays — the planner's auto-fill offers them, and the
// curated base locks them like days 1–3 when their spots are selected.
// Day 4: the Glyptoteket first (opens 10:00), then the walk down Istedgade to
// Enghave Plads, a proper smørrebrød lunch at a Vesterbro classic (Klubben,
// Frk. Barners Kælder or Axelborg Bodega), the Carlsberg Byen stroll and
// dinner in Kødbyen. Day 5: the coast line — Helsingør and Kronborg Castle
// first, then Louisiana in Humlebæk on the way back (the museum stays open
// late, the castle doesn't).
export const ITINERARY_45 = [
  ["glyptoteket", "istedgade", "enghaveplads", "smorrebrod", "carlsberg", "carlsbergbyen", "koedbyen"],
  ["helsingor", "louisiana"],
];
export const ITINERARY_ALL = [...ITINERARY_3D, ...ITINERARY_45];

// ---------------------------------------------------------------------------
// Visitor-added custom stops (from a pasted Google Maps link). Registered at
// runtime, kept in the auto-save payload and in shared plans. They get the
// cluster of their nearest curated neighbour so they cluster sensibly.
// ---------------------------------------------------------------------------
const CUSTOM_SPOTS = new Map();

export function registerCustomSpot(spot) {
  if (!spot || !spot.id || !Array.isArray(spot.coords)) return;
  let cluster = "indre-by";
  let best = Infinity;
  SPOTS.forEach((s) => {
    const d = (s.coords[0] - spot.coords[0]) ** 2 + (s.coords[1] - spot.coords[1]) ** 2;
    if (d < best) {
      best = d;
      cluster = s.cluster;
    }
  });
  CUSTOM_SPOTS.set(spot.id, {
    dur: 1,
    durLabel: "~1h",
    seasons: ALL,
    order: 2.5,
    note: "Your own stop",
    custom: true,
    ...spot,
    cluster,
  });
}

export const spotById = (id) => SPOTS.find((s) => s.id === id) || CUSTOM_SPOTS.get(id);

export const MONTHS = [
  { id: 1, label: "Jan", full: "January" },
  { id: 2, label: "Feb", full: "February" },
  { id: 3, label: "Mar", full: "March" },
  { id: 4, label: "Apr", full: "April" },
  { id: 5, label: "May", full: "May" },
  { id: 6, label: "Jun", full: "June" },
  { id: 7, label: "Jul", full: "July" },
  { id: 8, label: "Aug", full: "August" },
  { id: 9, label: "Sep", full: "September" },
  { id: 10, label: "Oct", full: "October" },
  { id: 11, label: "Nov", full: "November" },
  { id: 12, label: "Dec", full: "December" },
];

// Typical weather + what to wear/bring, per month (Copenhagen)
// Temperatures mirror the Seasons page: Winter (Nov–Feb) -8–7°C, Spring (Apr–May) 8–16°C, Summer (Jun–Aug) 17–26°C, Autumn (Sep–Oct) 8–15°C
export const MONTH_INFO = {
  1: { temp: "-8–5°C", pack: "A proper winter coat, waterproof winter boots, rain jacket, hat & gloves", note: "It's the darkest and most quiet month. If you're lucky, you might get some snow." },
  2: { temp: "-8–5°C", pack: "A proper winter coat, waterproof winter boots, rain jacket, hat & gloves", note: "Wintry, dark and often windy — a good month for museums, cafés and cosy indoor hygge." },
  3: { temp: "1–6°C", pack: "A proper winter coat for early March — by month's end a warm jacket is enough. Waterproof boots, rain jacket, hat & gloves", note: "Chilly, changeable and often windy. Expect some rain or sleet." },
  4: { temp: "8–14°C", pack: "Jacket, layers and a rain jacket", note: "Spring arrives - blossoms and brighter days." },
  5: { temp: "10–16°C", pack: "Layers, a light jacket, light rain jacket, sunglasses + your camera", note: "Mild with long days and green parks." },
  6: { temp: "18–25°C", pack: "Swimwear, sunglasses, light rain jacket and a jumper for late nights", note: "Long daylight and harbour swims." },
  7: { temp: "19–26°C", pack: "Swimwear, sunglasses and a light layer", note: "Peak summer - harbour swims, beaches and festivals." },
  8: { temp: "14–23°C", pack: "Layers, a light rain jacket and swimwear", note: "Warm summer days hopefully continue…" },
  9: { temp: "14–19°C", pack: "Layers and a light rain jacket", note: "Golden early autumn — mild and quieter than peak summer. Some rain might kick in." },
  10: { temp: "10–12°C", pack: "Coat, layers, rain jacket and maybe waterproof shoes/boots", note: "Crisp autumn, orange leaves and candlelit hygge. Can be rainy." },
  11: { temp: "3–7°C", pack: "A proper coat, hat & scarf + a rain jacket and maybe waterproof shoes/boots", note: "Kinda cold and might be rainy, but Christmas lights are warming and markets open start/mid-month." },
  12: { temp: "1–5°C", pack: "A proper winter coat, hat & gloves + maybe waterproof shoes/boots", note: "Full Christmas season — glögg and lights." },
};

// Optional italic "pro tip" shown under the month event note
export const MONTH_TIPS = {
  2: "It might be windy - keep warm!",
  4: "Cherry blossom season in late April.",
  5: "Reffen street food is the perfect hang-out spot",
  7: "Bring sunscreen and patience!",
  8: "Take the harbour bus from Nyhavn to Sluseholmen and back for the scenery.",
  11: "Taste real Glögg instead of Glühwein!",
  12: "Taste real Glögg instead of Glühwein!",
};

// Headline seasonal event per month (for the planner's when-you-visit note)
export const MONTH_EVENTS = {
  2: "The Copenhagen Light Festival lights up the dark — three weeks of light art across the city.",
  5: "Copenhagen Marathon winds through the city this month.",
  6: "Distortion street parties take over parts of town for a week.",
  11: "Christmas markets start opening — glögg and festive stalls appear.",
  12: "25 + 26 December: many things are closed and transport runs at low capacity.",
};

export const CLUSTERS = {
  "indre-by": "Indre By & the old town",
  harbour: "The harbour & Nyhavn",
  christianshavn: "Christianshavn",
  "islands-brygge": "Islands Brygge",
  refshaleoen: "Refshaleøen",
  vesterbro: "Vesterbro",
  norrebro: "Nørrebro",
  nordvest: "Nordvest",
  osterbro: "Østerbro",
  amager: "Amager",
  frederiksberg: "Frederiksberg",
  daytrip: "Out of town",
};

// Availability is a list of month numbers a spot is open/worthwhile
const ALL = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const SWIM = [6, 7, 8]; // harbour swimming: Jun–Aug
const XMAS = [11, 12]; // Christmas markets: Nov–Dec

// dur = realistic hours to "set off" for it; order = time-of-day flow (1 morning, 2 lunch, 3 afternoon, 4 evening); coords used to keep nearby stops on the same day
export const SPOTS = [
  // Indre By
  { id: "rundetaarn", name: "Rundetårn", cluster: "indre-by", dur: 1, seasons: ALL, order: 1.1, coords: [55.6813, 12.5757], note: "Climb the 17th-century Round Tower for rooftop views" },
  { id: "rosenborg", name: "Rosenborg Castle", cluster: "indre-by", gyg: "https://gyg.me/Sg1VJNVD", dur: 1.5, seasons: ALL, order: 1.2, coords: [55.6857, 12.5772], note: "Renaissance castle & the crown jewels", open: "Summer (27 Jun–30 Aug): daily 09:00–17:00. Rest of the year: Tue–Sun 10:00–17:00, closed Mondays." },
  { id: "kongenshave", name: "Kongens Have", cluster: "indre-by", dur: 1, seasons: ALL, order: 1.22, note: "Copenhagen's oldest royal garden, behind Rosenborg Castle — lawns, rose beds and a summer puppet theatre; a favourite local picnic spot.", coords: [55.6857, 12.58] },
  { id: "smk", name: "SMK · National Gallery", cluster: "indre-by", dur: 2, seasons: ALL, order: 1.25, coords: [55.6886, 12.5786], note: "Denmark's national gallery — 700 years of art from Rembrandt to Matisse, right by Kongens Have", open: "Open Tue–Sun 10:00–18:00 (Wed until 20:00), closed Mondays. The permanent collection is free." },
  { id: "botaniskhave", name: "Botanisk Have & Palm House", cluster: "indre-by", dur: 1, seasons: ALL, order: 1.24, coords: [55.6868, 12.574], note: "Copenhagen's free botanical garden — old trees, winding paths and the iconic 1874 glasshouse, right by Rosenborg & SMK", open: "The garden is free and open daily — 8:30–18:00 (1 Mar–24 Oct), 8:30–16:00 (25 Oct–28 Feb). The Palm House is ticketed and usually closed Mondays.", tip: "Book your Palm House entry ahead in summer — it sells out on sunny days." },
  { id: "stroget", name: "Stroll & shop Strøget", cluster: "indre-by", hidePin: true, dur: 1, seasons: ALL, order: 1.4, note: "The long pedestrian shopping street through the centre — shops, buskers and squares, running from Rådhuspladsen to Kongens Nytorv.", coords: [55.67856, 12.57928] },
  { id: "magstraede", name: "Wander Magstræde", cluster: "indre-by", hidePin: true, dur: 0.33, durLabel: "~20 min", seasons: ALL, order: 1.5, coords: [55.67638, 12.57549], note: "One of Copenhagen's oldest cobbled streets, just off Strøget by Gammel Strand" },
  { id: "latinquarter", name: "Stroll the Latin Quarter", cluster: "indre-by", hidePin: true, dur: 1, seasons: ALL, order: 1.35, coords: [55.67831, 12.56966], note: "Cobbled lanes, bookshops & cafés around the old university and Vor Frue Kirke" },
  { id: "amalienborg", name: "Amalienborg", cluster: "harbour", dur: 0.5, durLabel: "~30 min", seasons: ALL, order: 0.55, coords: [55.6841, 12.5934], note: "The royal palace — changing of the guard at noon" },
  { id: "marmorkirken", name: "Marmorkirken", cluster: "harbour", dur: 0.25, durLabel: "~15 min", seasons: ALL, order: 0.5, coords: [55.685, 12.5894], note: "The domed Marble Church — a 2-min walk from Amalienborg" },
  { id: "designmuseum", name: "Designmuseum Danmark", cluster: "harbour", dur: 1.5, seasons: ALL, order: 1.3, coords: [55.68626, 12.59049], note: "Denmark's home of design — iconic chairs, lamps & Danish Modern, near Marmorkirken", open: "Open Tue–Sun 10:00–18:00 (Wed until 20:00), closed Mondays." },
  { id: "torvehallerne", name: "Torvehallerne market", cluster: "indre-by", dur: 1, seasons: ALL, order: 1.05, coords: [55.6838, 12.5695], note: "Copenhagen's glass-roofed food market — ~60 stalls of smørrebrød, pastries, coffee, cheese, fresh produce and hot food. A great breakfast or lunch graze.", open: "Open daily from 10:00 — closing 19:00 on weekdays and 18:00 at weekends; a strolling, breakfast, lunch & hang-out spot, not a dinner place." },
  { id: "sanktpeders", name: "Sankt Peders Bageri", cluster: "indre-by", crawl: true, dur: 0.33, durLabel: "~20 min", seasons: ALL, order: 1, coords: [55.67913, 12.56921], note: "CPH's oldest bakery — giant cinnamon buns on Wednesdays", open: "Open year-round, but closed on Sundays." },
  { id: "laglace", name: "La Glace", cluster: "indre-by", crawl: true, dur: 1, seasons: ALL, order: 1.45, coords: [55.6786, 12.5735], note: "Denmark's oldest confectionery — sit-down layer cakes under chandeliers, right by Strøget. Try a 'Lagkage' (Danish layer cake) and order a hot chocolate.", open: "Open daily, roughly 10:00–18:00 (a little later on Fridays).", tip: "Booking a table is advised." },
  { id: "smorrebrod", name: "Smørrebrød / Danish lunch restaurant", cluster: "indre-by", hidePin: true, dur: 1.25, seasons: ALL, order: 2.9, coords: [55.6805, 12.585], note: "Classic venues all over town — Schønnemann, Aamanns 1921, Told & Snaps, or out west: Klubben, Dolores, Aamanns Genbo, Krøgers Familiehave" },
  { id: "gasolinegrill", name: "Gasoline Grill burger", cluster: "indre-by", hidePin: true, dur: 0.5, durLabel: "~30 min", seasons: ALL, order: 2.9, note: "Cult Copenhagen burger joint that started in an old petrol station — a short menu of top-quality burgers; expect a quick queue.", coords: [55.6795, 12.579] },
  { id: "thorvaldsens", name: "Thorvaldsens Museum", cluster: "indre-by", dur: 1.5, seasons: ALL, order: 1.3, coords: [55.67588, 12.57826], note: "Vivid, colour-drenched halls of Bertel Thorvaldsen's sculpture — right by Christiansborg", open: "Open Tue–Sun 10:00–17:00, closed Mondays." },
  { id: "glyptoteket", name: "Ny Carlsberg Glyptotek", cluster: "indre-by", dur: 1.5, seasons: ALL, order: 2.6, coords: [55.6726, 12.5713], note: "A glorious art museum by Tivoli — ancient Egyptian, Greek & Roman sculpture, French Impressionists and a palm-filled winter garden under a glass dome", open: "Open Tue–Sun 10:00–17:00 (Thu until 21:00), closed Mondays." },
  { id: "royallibrarygarden", name: "The Royal Library Garden", cluster: "indre-by", dur: 0.5, durLabel: "~30 min", seasons: ALL, order: 1.35, coords: [55.6739, 12.5817], note: "A tranquil hidden garden behind Christiansborg — pond, willow trees and a Kierkegaard statue" },
  { id: "sortdiamant", name: "Den Sorte Diamant", cluster: "indre-by", dur: 0.5, durLabel: "~30 min", seasons: ALL, order: 1.36, coords: [55.6739, 12.5821], note: "The Royal Library's black granite-and-glass harbourfront wing — step into the soaring atrium for free, right next to the Library Garden", open: "The atrium is free to enter — open daily, late (to 20:00) on weekdays, with earlier closing on Fridays and at weekends." },
  { id: "nationalmuseet", name: "Nationalmuseet", cluster: "indre-by", gyg: "https://gyg.me/SXvpX4fb", dur: 2, seasons: ALL, order: 1.32, coords: [55.6747, 12.5744], note: "Denmark's biggest history museum in a royal prince's palace — Viking treasures, the Sun Chariot & Egyptian mummies", open: "Open 10:00–17:00 — daily from April to October; closed Mondays from November to March. The Children's Museum closes at 16:30 — kids go free with a paying adult." },
  { id: "kbhmuseum", name: "Københavns Museum", cluster: "indre-by", dur: 1.5, seasons: ALL, order: 2.65, coords: [55.6744, 12.5724], note: "The story of Copenhagen itself — from Viking settlement to modern city, just behind the City Hall", open: "Open Mon–Wed & Fri 10:00–17:00, Thu until 20:00, Sat–Sun 11:00–17:00 — special hours over Christmas and New Year." },
  { id: "dac", name: "Danish Architecture Center", cluster: "indre-by", gyg: "https://gyg.me/ms7UuAEM", dur: 1.5, seasons: ALL, order: 2.7, coords: [55.6724, 12.5789], note: "Architecture & city life inside the striking harbourfront BLOX building — changing exhibitions, a big café and a slide for the kids", open: "Open daily, roughly 10:00–18:00 (later on Thursdays)." },
  { id: "christiansborg", name: "Christiansborg Palace", cluster: "indre-by", gyg: "https://gyg.me/7NHDT7yb", dur: 1, seasons: ALL, order: 1.37, coords: [55.6759, 12.5797], note: "The parliament palace on Slotsholmen — royal reception rooms, ruins beneath, and a free tower view over the city", open: "Palace rooms open Tue–Sun 10:00–17:00 (closed Mondays); the tower viewpoint is free." },
  { id: "graabroedretorv", name: "Gråbrødretorv", cluster: "indre-by", dur: 0.25, durLabel: "~15 min", seasons: ALL, order: 1.36, coords: [55.6796, 12.5762], note: "A cobbled, café-lined square — one of the oldest and prettiest corners of the old town" },
  { id: "nyboder", name: "Nyboder", cluster: "harbour", dur: 0.75, durLabel: "~45 min", seasons: ALL, order: 2.3, coords: [55.6878, 12.5895], note: "Rows of ochre-yellow 17th-century naval terraces — a photogenic pocket of old Copenhagen" },
  { id: "krusemyntegade", name: "Krusemyntegade", cluster: "harbour", hidePin: true, dur: 0.25, durLabel: "~15 min", seasons: ALL, order: 2.4, coords: [55.6885, 12.5846], note: "A short rainbow of small rowhouses just outside Kastellet's ramparts", tip: "Pair it with the walk around the star fortress." },
  { id: "tivoli", name: "Tivoli Gardens", cluster: "indre-by", gyg: "https://gyg.me/3HlBmZwt", dur: 4, seasons: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12], hint: "Mar–Dec", order: 4, durLabel: "3–5h", note: "The famous 1843 pleasure garden in the heart of the city — rides, gardens, concerts and restaurants; magical when lit up at night.", coords: [55.67465, 12.56566], open: "Tivoli opens in seasons: the summer season starts 19 March (with the Easter season 19–29 March) and runs to ~20 Sep, then Halloween in October and Christmas from mid-November to ~3 January (closed February–mid-March).", tip: "Avoid Friday nights in summer: entry is 180 kr. before 18:00 but jumps to 275 kr. after — and a summer concert pack the place. Come earlier or preferably pick another evening." },
  // Harbour
  { id: "nyhavn", name: "Nyhavn", cluster: "harbour", dur: 0.75, durLabel: "~45 min", seasons: ALL, order: 1, coords: [55.67963, 12.5913], note: "Copenhagen's iconic 17th-century harbour — a short canal lined with candy-coloured townhouses, old wooden sailing ships and quayside bars. The city's most photographed spot." },
  { id: "canaltour", name: "Canal boat tour", cluster: "harbour", gyg: "https://gyg.me/d4it0SF2", dur: 1, seasons: ALL, order: 1.02, coords: [55.68077, 12.58817], note: "Departs Nyhavn — allow ~15 min for the next boat" },
  { id: "broens", name: "Broens Gadekøkken", cluster: "harbour", dur: 0.75, durLabel: "~45 min", seasons: ALL, order: 2, coords: [55.67786, 12.59682], note: "Harbourfront street-food market right across from Nyhavn — burgers, tacos, bao, bowls and bars with canal-side seating.", open: "Open year-round — a summer season from mid-March to October and a winter season from November to late February." },
  { id: "littlemermaid", name: "Little Mermaid & Kastellet", cluster: "harbour", dur: 1, seasons: ALL, order: 0.6, coords: [55.6929, 12.5993], note: "The famous statue + the star-shaped fortress park — a 10–15 min waterfront walk up from Amalienborg" },
  // Østerbro
  { id: "juno", name: "Juno the Bakery", cluster: "osterbro", crawl: true, dur: 0.33, durLabel: "~20 min", seasons: ALL, order: 1, coords: [55.70616, 12.58169], note: "Minimalist Østerbro bakery famous for its cardamom buns — arrive early, queues get long. (Old-school Bosses nearby is a good comparison and backup.)" },
  { id: "ismageriet", name: "Ismageriet ice cream", cluster: "osterbro", dur: 0.33, durLabel: "~20 min", seasons: ALL, order: 2.5, coords: [55.69792, 12.57483], note: "A beloved local ice-cream shop — proper Danish ice, freshly made cones and generous scoops. A perfect Østerbro treat stop." },
  { id: "bosses", name: "Bosses Bager", cluster: "osterbro", crawl: true, dur: 0.33, durLabel: "~20 min", seasons: ALL, order: 1, coords: [55.70616, 12.57770], note: "No-nonsense Østerbro bakery beloved for good-value pastries" },
  { id: "brumleby", name: "Brumleby", cluster: "osterbro", dur: 0.5, durLabel: "~30 min", seasons: ALL, order: 2.2, coords: [55.7015, 12.5756], note: "A hidden 1850s workers' village of butter-yellow terraces — one of Østerbro's most charming corners" },
  { id: "kartoffelraekkerne", name: "Kartoffelrækkerne", cluster: "osterbro", dur: 0.5, durLabel: "~30 min", seasons: ALL, order: 2.3, coords: [55.6924, 12.5725], note: "The 'Potato Rows' — leafy lanes of Victorian terraced houses that locals adore" },
  { id: "olufsgade", name: "Olufsgade", cluster: "osterbro", dur: 0.42, durLabel: "~25 min", seasons: ALL, order: 2.4, coords: [55.7010, 12.5766], note: "A pocket of old working-class Østerbro — modest, colourful and quietly pretty" },
  { id: "nordhavn", name: "Stroll Nordhavn", cluster: "osterbro", hidePin: true, dur: 2, seasons: ALL, order: 3, note: "A walk around the regenerated harbour district — modern architecture, waterfront promenades, an 8-floor-high playground/fitness roof with an amazing view and lots of cafés and restaurants.", coords: [55.70767, 12.59612] },
  { id: "experimentarium", name: "Experimentarium", cluster: "osterbro", gyg: "https://gyg.me/14p7Y8sH", dur: 3, durLabel: "2–4h", seasons: ALL, order: 2, coords: [55.7267, 12.5801], note: "A world of hands-on science in Hellerup — bubbles, illusions and experiments. A huge hit with kids and a rainy-day saver", open: "Open every day, year-round, 09:30–17:00 — Tuborg Havnevej 7, Hellerup.", tip: "S-tog A, C, E or F (or the Øresund train) to Hellerup Station, then walk or bus 1A/21/164 — or S-tog to Svanemøllen Station and bus 1A. Get off at Tuborg Boulevard (Strandvejen)." },
  // Christianshavn
  { id: "canalstroll", name: "Stroll the canal", cluster: "christianshavn", hidePin: true, dur: 1, seasons: ALL, order: 3, coords: [55.67271, 12.59158], note: "An easy waterside wander along Christianshavn's main canal — houseboats, little bridges, cafés and the prettiest stretch of old Copenhagen, right by Christiania." },
  { id: "amagergade", name: "Amagergade", cluster: "christianshavn", hidePin: true, dur: 0.42, durLabel: "~25 min", seasons: ALL, order: 3.1, coords: [55.6708, 12.5929], note: "Crooked old houses leaning over the cobbles — Christianshavn at its most storybook" },
  { id: "sofiegade", name: "Sofiegade", cluster: "christianshavn", hidePin: true, dur: 0.08, durLabel: "~5 min", seasons: ALL, order: 3.15, coords: [55.6713, 12.59], note: "The cheerfully hand-painted, stripey house at no. 4 — one special facade, and the lane around it is quiet and sweet", tip: "Swing by on your way along the canals." },
  { id: "christiania", name: "Freetown Christiania", cluster: "christianshavn", dur: 1.5, seasons: ALL, order: 3.3, coords: [55.6734, 12.5999], note: "Car-free, mural-covered alternative community" },
  { id: "oursaviour", name: "Our Saviour's spire climb", cluster: "christianshavn", dur: 1, seasons: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12], hint: "Feb–Dec", hideOffSeason: true, order: 3.1, note: "Baroque church with an external spiral staircase winding up the spire — 400 steps to sweeping rooftop views over the city.", coords: [55.67279, 12.59405], open: "The spire climb is open February–December (closed January).", tip: "Book a time slot online in advance — climbs sell out fast, especially in summer and weekends." },
  { id: "opera", name: "Opera House & Park", cluster: "christianshavn", gyg: "https://gyg.me/dIjRgguZ", dur: 0.75, durLabel: "~45 min", seasons: ALL, order: 3.6, coords: [55.6819, 12.6], noAreaPin: true, note: "The modern harbour opera house + the lush new Opera Park — free to wander, right on the water" },
  // Refshaleøen
  { id: "copenhill", name: "CopenHill hike or ski", cluster: "refshaleoen", gyg: "https://gyg.me/gbJD4cCp", dur: 2, seasons: ALL, order: 3, note: "A power plant you can climb — a rooftop hiking trail, a real dry ski slope and the world's tallest climbing wall, out on Amager.", coords: [55.6844, 12.62] },
  { id: "reffen", name: "Reffen street food", cluster: "refshaleoen", dur: 2, seasons: [3, 4, 5, 6, 7, 8, 9, 10], hint: "Mar–Oct", hideOffSeason: true, order: 3.5, note: "Copenhagen's biggest street-food market, out on the harbour at Refshaleøen — dozens of stalls, bars and container shops (summer season).", coords: [55.693, 12.607], open: "Reffen's street-food season runs from mid-March into late October." },
  { id: "ismageriet-refshaleoen", name: "Ismageriet (Refshaleøen)", cluster: "refshaleoen", dur: 0.33, durLabel: "~20 min", seasons: [3, 4, 5, 6, 7, 8, 9, 10], hint: "Mar–Oct", order: 3.55, coords: [55.6923, 12.6058], note: "The beloved ice cream, with a seasonal stall out on Reffen", open: "The Refshaleøen Ismageriet is seasonal — open from mid-March into late October." },
  { id: "cc", name: "Copenhagen Contemporary", cluster: "refshaleoen", gyg: "https://gyg.me/Et9CZJrN", dur: 1.5, seasons: ALL, order: 3.4, coords: [55.6913, 12.611], note: "Monumental, immersive art — light-and-sound installations you walk through, in a vast former welding hall on Refshaleøen", open: "Open Tue–Sun, roughly 11:00–18:00 — closed Mondays." },
  // Islands Brygge
  { id: "harbourbath", name: "Islands Brygge Harbour Bath", cluster: "islands-brygge", dur: 2, seasons: SWIM, hideOffSeason: true, order: 3, note: "The original open-air harbour pool — swim right in the clean city harbour, with lawns and a buzzing local crowd on warm days.", coords: [55.66863, 12.5778], open: "Harbour swimming is best June–August." },
  { id: "havnevigen", name: "Havnevigen", cluster: "islands-brygge", dur: 2, seasons: SWIM, hideOffSeason: true, order: 3.1, coords: [55.6545, 12.5656], note: "A newer sandy harbour beach on Islands Brygge", open: "Harbour beach — best June–August." },
  { id: "andersen", name: "Andersen Bakery", cluster: "islands-brygge", crawl: true, dur: 0.33, durLabel: "~20 min", seasons: ALL, order: 1, coords: [55.667138, 12.578329], note: "A must-try high-end bakery on Islands Brygge — tucked into a side street behind the harbourfront. Cakes, pastries & coffee" },
  { id: "goboat", name: "GoBoat harbour picnic", cluster: "islands-brygge", dur: 2, seasons: [3, 4, 5, 6, 7, 8, 9, 10], hint: "Mar–Oct", order: 3.9, coords: [55.6691, 12.5786], note: "Captain your own little picnic boat around the canals and harbour — no licence needed, room for 8", open: "Islands Brygge 10 — open every day 09:30 to sunset.", tip: "Book ahead in summer — boats sell out fast on sunny days." },
  // Nørrebro
  { id: "jaegersborggade", name: "Jægersborggade", cluster: "norrebro", dur: 0.75, durLabel: "~45 min", seasons: ALL, order: 3, coords: [55.6927, 12.5439], note: "A 300m indie street in Nørrebro — specialty coffee, natural-wine bars, ceramics and bakeries like the Coffee Collective and Meyers Bageri." },
  { id: "superkilen", name: "Superkilen", cluster: "norrebro", dur: 0.5, durLabel: "~30 min", seasons: ALL, order: 3, coords: [55.6992, 12.5417], note: "Nørrebro's famous urban park — the red square, the black square with its sweeping white lines, and quirky objects from 60+ countries", open: "A public park — always open, always free." },
  { id: "assistens", name: "Assistens Kirkegård", cluster: "norrebro", dur: 1, seasons: ALL, order: 3, coords: [55.6911, 12.5494], note: "A leafy cemetery-park where locals stroll, picnic and sunbathe — Hans Christian Andersen and Søren Kierkegaard are buried here." },
  { id: "grundtvigs", name: "Grundtvigs Kirke", cluster: "nordvest", dur: 0.75, durLabel: "~45 min", seasons: ALL, order: 2.9, coords: [55.71661, 12.53382], note: "A jaw-dropping expressionist church built entirely from pale yellow brick — worth the trip north", open: "Open most days, but hours vary.", tip: "Check their website before you go — it can be closed on days with a church service." },
  { id: "bispebjerg", name: "Bispebjerg Kirkegård", cluster: "nordvest", dur: 1.5, seasons: ALL, order: 2.95, coords: [55.71259, 12.52271], note: "The famous cherry-blossom avenue — a pink tunnel in mid/late April, peaceful greenery the rest of the year" },
  { id: "blaagaards", name: "Blågårdsgade", cluster: "norrebro", dur: 0.75, durLabel: "~45 min", seasons: ALL, order: 3, coords: [55.68616, 12.55806], note: "A laid-back Nørrebro street and square — neighbourhood cafés, bars and a real local feel away from the tourist track." },
  { id: "diamondpizza", name: "Diamond slice", cluster: "norrebro", dur: 0.25, seasons: ALL, order: 3, durLabel: "~15 min", coords: [55.68616, 12.55806], note: "New York-style sourdough-based pizza by the slice — cheap, tasty and perfect grab-and-go in Nørrebro." },
  // Vesterbro
  { id: "istedgade", name: "Stroll Istedgade", cluster: "vesterbro", dur: 1, seasons: ALL, order: 3, coords: [55.66907, 12.55334], note: "Vesterbro's liveliest street — cafés, bars, shops and people-watching" },
  { id: "enghaveplads", name: "Enghave Plads", cluster: "vesterbro", dur: 0.5, durLabel: "~30 min", seasons: ALL, order: 3.2, coords: [55.6672, 12.5468], note: "The square at the end of Istedgade — Vesterbro's living room: a fountain, plane trees, and local bars & cafés all around" },
  { id: "koedbyen", name: "Kødbyen (Meatpacking)", cluster: "vesterbro", dur: 2, seasons: ALL, order: 3.8, coords: [55.66777, 12.55839], note: "The Meatpacking District's restaurant row — dinner-friendly, kitchens and bars run late into the evening" },
  { id: "ismageriet-kodbyen", name: "Ismageriet (Kødbyen)", cluster: "vesterbro", dur: 0.33, durLabel: "~20 min", seasons: ALL, order: 3.9, coords: [55.66801, 12.56071], note: "The beloved ice cream, with a stall in the Meatpacking District" },
  // Frederiksberg
  { id: "zoo", name: "Copenhagen Zoo", cluster: "frederiksberg", gyg: "https://gyg.me/W3Senqr2", dur: 4.5, durLabel: "4–5h", seasons: ALL, order: 2, coords: [55.6725, 12.523], closesByMonth: { 1: 16, 2: 16, 3: 16, 4: 17, 5: 17, 6: 18, 7: 20, 8: 18, 9: 18, 10: 17, 11: 16, 12: 16 }, note: "Elephants, pandas & the historic wooden tower — a half-day out west in Frederiksberg", open: "Closes earlier in winter — around 16:00 from November to March, 17:00 in spring & autumn, and as late as 18:00–20:00 in summer.", tip: "Both getting out to the Zoo and the Zoo itself take time — don't pack too much in before you go. In winter, aim to arrive early." },
  { id: "frederiksberghave", name: "Frederiksberg Have", cluster: "frederiksberg", dur: 1.5, seasons: ALL, order: 1.5, coords: [55.675, 12.524], note: "A romantic landscaped park with canals, a hilltop palace and free-roaming herons — a locals' picnic favourite" },
  { id: "cisternerne", name: "Cisternerne", cluster: "frederiksberg", dur: 1, seasons: ALL, order: 2.5, coords: [55.6696, 12.5242], note: "Art deep underground — the old water reservoirs beneath Søndermarken, with dripstones, total darkness and a 17-second echo", open: "Open Mon–Tue & Fri–Sun 11:00–18:00, Wed–Thu until 20:00 — but closed Mondays in October and November.", tip: "It's cold and humid down there year-round — bring a jumper, even in summer." },
  { id: "carlsberg", name: "Home of Carlsberg", cluster: "vesterbro", gyg: "https://gyg.me/wg6bmKQ1", dur: 2, seasons: ALL, order: 3.3, coords: [55.6647, 12.5283], note: "The Carlsberg story in the historic brewhouse — the exhibition, the dray-horse stables and the famous Elephant Gate", open: "Open every day 10:00–18:00 — last entry to the exhibition is 16:00 (two hours before closing).", tip: "The exhibition ticket includes a 40 cl Carlsberg draft — or a soda/water." },
  { id: "carlsbergbyen", name: "Stroll Carlsberg Byen", cluster: "vesterbro", hidePin: true, dur: 1, seasons: ALL, order: 3.25, coords: [55.6659, 12.5357], note: "The brewery's old grounds turned Copenhagen's newest district — historic brick, hidden courtyards, silo bars and bold new towers" },
  // Christmas markets (winter only) — temporary events, not permanent places
  { id: "xmas-kongensnytorv", name: "Christmas market · Kongens Nytorv", cluster: "indre-by", event: true, dur: 0.5, seasons: XMAS, hideOffSeason: true, order: 3.5, durLabel: "~30 min", coords: [55.6805, 12.5851], note: "Danish specialities & gløgg", open: "Christmas markets run from mid-November to late December. Some close early on weekdays.", openAnon: true },
  { id: "xmas-radhuspladsen", name: "Christmas market · Rådhuspladsen", cluster: "indre-by", event: true, dur: 0.5, seasons: XMAS, hideOffSeason: true, order: 3.6, durLabel: "~30 min", note: "Large Christmas market on City Hall Square — stalls, lights, gløgg and fairground rides.", coords: [55.6759, 12.5701], open: "Christmas markets run from mid-November to late December. Some close early on weekdays.", openAnon: true },
  { id: "xmas-hojbro", name: "Christmas market · Højbro Plads", cluster: "indre-by", event: true, dur: 0.5, seasons: XMAS, hideOffSeason: true, order: 3.65, durLabel: "~30 min", note: "New for 2026 — cosy stalls and stories from across the Danish Realm (Greenland and the Faroes included), with music, Christmas entertainment and family activities.", coords: [55.6779, 12.5802], open: "14 November – 20 December 2026. Mon–Wed 11:00–19:00, Thu until 20:00, Fri–Sat until 21:00, Sun 12:00–19:00.", openAnon: true },
  { id: "xmas-gammeltorv", name: "Christmas market · Gammel Torv", cluster: "indre-by", event: true, dur: 0.5, seasons: XMAS, hideOffSeason: true, order: 3.7, durLabel: "~30 min", note: "Festive Christmas market on the old square Gammeltorv — crafts, gløgg and seasonal treats.", coords: [55.6781, 12.5722], open: "Christmas markets run from mid-November to late December. Some close early on weekdays.", openAnon: true },
  { id: "hviids", name: "Gløgg at Hviids Vinstue", cluster: "indre-by", event: true, dur: 0.5, seasons: XMAS, hideOffSeason: true, order: 3.45, durLabel: "~30 min", coords: [55.6798, 12.5855], note: "The gløgg institution — Copenhagen's oldest wine cellar (1723), right on Kongens Nytorv", open: "A Christmas-season tradition; the cellar itself is open year-round." },
  { id: "dangleterre", name: "d'Angleterre's Christmas facade", cluster: "indre-by", event: true, hidePin: true, dur: 0.083, seasons: XMAS, hideOffSeason: true, order: 3.42, durLabel: "~5 min", coords: [55.6795, 12.5864], note: "The grand hotel's Christmas facade — the city's most extra decorations; a quick look on the way past", open: "Lights switched on 13 November at 16:45, then lit through the Christmas season — best after dark." },
  // Bakery stops (crawl: true) — hidden behind the picker's bakery toggle.
  // Open year-round; only the one-tap BAKERY_WALK fill is Fastelavn-seasoned.
  { id: "detrenebrod", name: "Det Rene Brød", cluster: "osterbro", crawl: true, dur: 0.33, durLabel: "~20 min", seasons: ALL, order: 1, coords: [55.7040, 12.5767], note: "Organic sourdough and seasonal buns" },
  { id: "buka", name: "Buka", cluster: "indre-by", crawl: true, dur: 0.33, durLabel: "~20 min", seasons: ALL, order: 1, coords: [55.67701, 12.57076], note: "Minimalist bakery — excellent cardamom buns and filled croissants" },
  { id: "andersenmaillard", name: "Andersen & Maillard", cluster: "indre-by", crawl: true, dur: 0.33, durLabel: "~20 min", seasons: ALL, order: 1, coords: [55.6803, 12.5835], note: "Croissant cubes and coffee — three shops across the city" },
  { id: "hart", name: "Hart Bageri", cluster: "indre-by", crawl: true, dur: 0.33, durLabel: "~20 min", seasons: ALL, order: 1, coords: [55.6830, 12.5783], note: "Cardamom croissants and sourdough — shops all over town" },
  { id: "lille", name: "Lille Bakery", cluster: "refshaleoen", crawl: true, dur: 0.33, durLabel: "~20 min", seasons: ALL, order: 1, coords: [55.6930, 12.6110], note: "Worth the trip to Refshaleøen for the perfect 'BMO' — no photos allowed inside", open: "Open Wednesday–Sunday 08:00–17:00 — closed Mondays and Tuesdays." },
  { id: "brod", name: "Bageriet BRØD", cluster: "vesterbro", crawl: true, dur: 0.33, durLabel: "~20 min", seasons: ALL, order: 1, coords: [55.6669, 12.5469], note: "The little organic bakery right on Enghave Plads — sourdough and morning buns" },
  { id: "kobenhavnsbageri", name: "Københavns Bageri", cluster: "vesterbro", crawl: true, noAreaPin: true, dur: 0.33, durLabel: "~20 min", seasons: ALL, order: 1, coords: [55.6648, 12.5355], note: "Proper Carlsberg Byen neighbourhood bakery — get a traditional kringle" },
  { id: "betterbakery", name: "Better Bakery", cluster: "vesterbro", crawl: true, dur: 0.33, durLabel: "~20 min", seasons: ALL, order: 1, coords: [55.6700, 12.5571], note: "Neighbourhood bakery on Helgolandsgade" },
  { id: "alice", name: "Alice", cluster: "amager", crawl: true, dur: 0.33, durLabel: "~20 min", seasons: ALL, order: 1, coords: [55.6628, 12.5835], note: "Small beloved Amager bakery on Markmandsgade" },
  { id: "eriks", name: "Eriks Bageri", cluster: "amager", crawl: true, dur: 0.33, durLabel: "~20 min", seasons: ALL, order: 1, coords: [55.6562, 12.6010], note: "Old-school Amager bakery on Amagerbrogade — the drømmekage is the one" },
  // The ready-made Christmas walk — one winter day: Nyhavn's market, gløgg at
  // Hviids, the Gammel Torv & Rådhuspladsen markets, and into Tivoli's lights
  // as it gets dark. Tivoli stays last (the evening-cap rule holds).
  // Used by the planner's seasonal one-tap fill.
  // NOTE: also mirrored as the "Christmas walk" route card on /christmas.
  // Summer bathing (summer only) — Islands Brygge harbour bath already listed above
  { id: "swim-sandkaj", name: "Sandkaj swim (Nordhavn)", cluster: "osterbro", hidePin: true, dur: 2, seasons: SWIM, hideOffSeason: true, order: 3, note: "A sunny harbour-bath deck in Nordhavn — ladders straight into clean harbour water; a local summer favourite.", coords: [55.7063, 12.5943] },
  { id: "swim-papiroen", name: "Papirøen", cluster: "harbour", dur: 0.75, durLabel: "~45 min", seasons: ALL, order: 3, coords: [55.6793, 12.5965], note: "Harbour-island stroll across the Butterfly bridge from Broens — harbour views, bold new architecture and a couple of cafés facing the Opera" },
  { id: "swim-amagerstrand", name: "Amager Strand swim", cluster: "amager", dur: 2, seasons: SWIM, hideOffSeason: true, order: 2.5, coords: [55.66298, 12.63851], note: "closest beach to Amager, Christianshavn & Nyhavn" },
  { id: "blaaplanet", name: "Den Blå Planet", cluster: "amager", dur: 2, seasons: ALL, order: 2.6, coords: [55.6381, 12.6561], note: "Northern Europe's largest aquarium — sharks glide overhead in the ocean tunnel, an easy metro ride from the centre", open: "Open Mon 10:00–21:00 and Tue–Sun 10:00–17:00 — and daily 10:00–21:00 in the summer high season.", tip: "Metro to Kastrup st., then a short walk. Buy tickets online ahead in summer and on weekends." },
  // Out of town (usually a full day on their own)
  { id: "bakken", name: "Bakken & Dyrehaven", cluster: "daytrip", page: "/trips/bakken", dur: 5, seasons: [4, 5, 6, 7, 8, 9, 10, 11, 12], hint: "Apr–Sep", order: 2, durLabel: "4–6h", coords: [55.77469, 12.57727], open: "Bakken opens weekends from April, daily from late May to mid/late August, then weekends until mid-September. It reopens for Halloween (10–18 & 23–25 Oct) and Christmas (21 Nov–20 Dec) — weekends only." },
  { id: "louisiana", name: "Louisiana Museum (Humlebæk)", cluster: "daytrip", page: "/trips/louisiana", dur: 3, seasons: ALL, order: 2, coords: [55.96918, 12.54230], open: "Open Tue–Fri 11:00–22:00, Sat–Sun 11:00–18:00 — closed Mondays.", note: "Denmark's famous modern-art museum on the Øresund coast — world-class art, a sculpture garden over the water and views across to Sweden. Open late midweek, so it's the perfect second half of a coast day after Kronborg." },
  { id: "frederiksborg", name: "Frederiksborg Castle (Hillerød)", cluster: "daytrip", page: "/trips/hillerod", dur: 4, seasons: ALL, order: 2, coords: [55.9349, 12.3006], note: "Denmark's grandest Renaissance castle on three lake islets — the Museum of National History, the chapel and the Baroque garden", open: "Museum open daily — 10:00–17:00 Apr–Oct, 11:00–15:00 Nov–Mar.", tip: "S-tog line A to Hillerød (~40 min), then a 20-minute walk through town on the walking street." },
  { id: "roskilde", name: "Roskilde (Viking ships & cathedral)", cluster: "daytrip", page: "/trips/roskilde", dur: 4, durLabel: "4–5h", seasons: ALL, order: 2, coords: [55.6502, 12.0804] },
  { id: "helsingor", name: "Helsingør & Kronborg Castle", cluster: "daytrip", page: "/trips/helsingor", dur: 4.5, seasons: ALL, order: 2, durLabel: "4–5h", coords: [56.039, 12.621], note: "Kronborg itself takes max 2h — the rest is Helsingør town: the harbour and the old streets. Doing both this and Louisiana in one day works fine — it just depends how long you give the town." },
  { id: "malmo", name: "Malmö, Sweden", cluster: "daytrip", page: "/trips/malmo", dur: 5, seasons: ALL, order: 2, durLabel: "4–6h", coords: [55.605, 13.0] },
  { id: "dragor", name: "Dragør fishing village", cluster: "daytrip", page: "/trips/dragor", dur: 3, seasons: ALL, order: 2, coords: [55.593, 12.673] },
];

// The ready-made Christmas walk — one winter day: Nyhavn's market, the
// Kongens Nytorv market, d'Angleterre's facade, gløgg at Hviids Vinstue, the
// Gammel Torv & Rådhuspladsen markets, then into Tivoli's lights as it gets
// dark (Tivoli stays last — the evening-cap rule holds).
// Used by the planner's seasonal one-tap fill; mirrored as the Christmas walk
// route card on /christmas.
export const XMAS_WALK = ["nyhavn", "xmas-kongensnytorv", "dangleterre", "hviids", "xmas-hojbro", "xmas-gammeltorv", "xmas-radhuspladsen", "tivoli"];

// The Fastelavn bakery crawl — the eight-stop route from /bakeries as one big
// tasting walk: Østerbro through the old town to the Latin Quarter (Juno right
// after Bosses, same as the page route). Shown as a one-tap fill in
// February–March (fastelavnsbolle season).
export const BAKERY_WALK = ["bosses", "juno", "detrenebrod", "buka", "andersenmaillard", "hart", "laglace", "sanktpeders"];

// Category per spot — used only to group the picker grid inside each area
// (strolls next to strolls, food next to food…). No effect on plans.
export const SPOT_CATS = {
  // sights & landmarks
  rundetaarn: "see", christiansborg: "see", amalienborg: "see",
  marmorkirken: "see", littlemermaid: "see", christiania: "see", opera: "see",
  grundtvigs: "see", sortdiamant: "see", superkilen: "see",
  // museums
  rosenborg: "museum", thorvaldsens: "museum", glyptoteket: "museum",
  designmuseum: "museum", cisternerne: "museum", smk: "museum",
  nationalmuseet: "museum", kbhmuseum: "museum", dac: "museum", cc: "museum",
  // strolls & streets
  kongenshave: "stroll", stroget: "stroll", magstraede: "stroll",
  latinquarter: "stroll", royallibrarygarden: "stroll", graabroedretorv: "stroll",
  nyboder: "stroll", nyhavn: "stroll", brumleby: "stroll", kartoffelraekkerne: "stroll",
  nordhavn: "stroll", canalstroll: "stroll", jaegersborggade: "stroll",
  carlsbergbyen: "stroll",
  sofiegade: "stroll", amagergade: "stroll", krusemyntegade: "stroll", olufsgade: "stroll",
  assistens: "stroll", bispebjerg: "stroll", blaagaards: "stroll", istedgade: "stroll",
  enghaveplads: "stroll",
  koedbyen: "stroll", frederiksberghave: "stroll", botaniskhave: "stroll",
  // food & drink
  torvehallerne: "food", smorrebrod: "food", gasolinegrill: "food", broens: "food",
  reffen: "food", diamondpizza: "food",
  // sweet stops
  sanktpeders: "treat", laglace: "treat", juno: "treat", ismageriet: "treat",
  bosses: "treat", "ismageriet-refshaleoen": "treat", andersen: "treat", "ismageriet-kodbyen": "treat",
  detrenebrod: "treat", buka: "treat", andersenmaillard: "treat", hart: "treat",
  lille: "treat", brod: "treat", kobenhavnsbageri: "treat",
  betterbakery: "treat", alice: "treat", eriks: "treat",
  // activities
  tivoli: "do", canaltour: "do", oursaviour: "do", copenhill: "do",
  harbourbath: "do", havnevigen: "do", zoo: "do", goboat: "do", carlsberg: "do",
  experimentarium: "do",
  "swim-sandkaj": "do", "swim-papiroen": "stroll", "swim-amagerstrand": "do",
  blaaplanet: "do",
  // seasonal events
  "xmas-kongensnytorv": "event", "xmas-radhuspladsen": "event", "xmas-gammeltorv": "event", "xmas-hojbro": "event",
  hviids: "event", dangleterre: "event",
  // out of town
  bakken: "trip", louisiana: "trip", roskilde: "trip", helsingor: "trip", malmo: "trip", dragor: "trip",
  frederiksborg: "trip",
};

const CAT_ORDER = ["see", "museum", "stroll", "food", "treat", "do", "event", "trip"];
export const CAT_LABELS = {
  see: "Sights & landmarks",
  museum: "Sights & landmarks", // museums share the sights header but sort after it (CAT_ORDER)
  stroll: "Strolls & streets",
  food: "Food & drink",
  treat: "Food & drink", // sweets share the food header but sort after it (CAT_ORDER)
  do: "Activities",
  event: "Seasonal events",
  trip: "Day trips",
};
export const spotCat = (id) => SPOT_CATS[id] || "see";
export const catRank = (id) => CAT_ORDER.indexOf(spotCat(id));

// Photo per spot (tourist recognition in the visual selector). Landmarks use real
// photos; food/street stops use tasteful representative photos. Ads/partners fall back.
const SPOT_IMAGES = {
  rundetaarn: "/planner/rundetaarn.webp",
  rosenborg: "https://images.unsplash.com/photo-1594907912929-884e283e28a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxSb3NlbmJvcmclMjBDYXN0bGUlMjBDb3BlbmhhZ2VufGVufDB8fHx8MTc4NzA1NzQwOXww&ixlib=rb-4.1.0&q=85",
  kongenshave: "/planner/d2f5d2ff264a.webp",
  stroget: "/planner/059cc52fb160.webp",
  magstraede: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/wv77dc9y_2026-09-28%2000_32_12.543%2B0200.webp",
  latinquarter: "/planner/05492535b60d.webp",
  amalienborg: "/planner/673556af846f.webp",
  marmorkirken: "https://images.pexels.com/photos/17603710/pexels-photo-17603710.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  torvehallerne: "/planner/229f24a483bf.webp",
  sanktpeders: "https://images.pexels.com/photos/38354172/pexels-photo-38354172.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  kobenhavnsbageri: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/34de60ce09a6f9a6_20261006_182008.jpg",
  buka: "https://images.unsplash.com/photo-1747459707229-7b1b7c8367e0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwzfHxmaWxsZWQlMjBjcm9pc3NhbnRzJTIwYmFrZXJ5JTIwY291bnRlcnxlbnwwfHx8fDE3OTEwNjc0NDd8MA&ixlib=rb-4.1.0&q=85",
  hart: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/vblx0mmk_20261004_210038.webp",
  laglace: "/planner/d42dfee2df14.webp",
  smorrebrod: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/xs7lrasl_20260823_083049%280%29.webp",
  gasolinegrill: "/planner/cf6b30b87209.webp",
  tivoli: "https://images.unsplash.com/photo-1646210201164-4cc951b59bbc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxjb3BlbmhhZ2VuJTIwdGl2b2xpfGVufDB8fHx8MTc4NzA1NzI2NHww&ixlib=rb-4.1.0&q=85",
  nyhavn: "/planner/115c33e22770.webp",
  canaltour: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/rppjvlko_2026-09-24%2011_56_58.503%2B0200.webp",
  broens: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/ed0ht3bb_2026-08-26%2011_57_17.441%2B0200.webp",
  littlemermaid: "https://images.unsplash.com/photo-1692388646650-5aedc9a8ed2f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxDb3BlbmhhZ2VuJTIwS2FzdGVsbGV0JTIwZm9ydHJlc3MlMjB3aW5kbWlsbHxlbnwwfHx8fDE3ODcyNTM4MTJ8MA&ixlib=rb-4.1.0&q=85&w=940&h=650&fit=crop",
  juno: "https://images.pexels.com/photos/38354165/pexels-photo-38354165.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  ismageriet: "/planner/62ab2b9ed816.webp",
  bosses: "/planner/90a6d4e6491f.webp",
  brumleby: "/planner/8140d7ae9a23.webp",
  kartoffelraekkerne: "/planner/29422848fd24.webp",
  nyboder: "/streets/nyboder.webp",
  sofiegade: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/z6es680k_20260926_175755.webp",
  amagergade: "/streets/amagergade.webp",
  krusemyntegade: "/streets/krusemyntegade.webp",
  olufsgade: "/streets/olufsgade.webp",
  graabroedretorv: "/streets/grabrodretorv.webp",
  royallibrarygarden: "https://images.pexels.com/photos/17077262/pexels-photo-17077262.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  nordhavn: "/planner/3e9ac976b335.webp",
  canalstroll: "/planner/056967bb7024.webp",
  christiania: "/planner/6a1a9f179183.webp",
  oursaviour: "https://images.unsplash.com/photo-1731541003483-cc75962d42f0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxDaHVyY2glMjBvZiUyME91ciUyMFNhdmlvdXIlMjBzcGlyZSUyMENvcGVuaGFnZW58ZW58MHx8fHwxNzg3MDU3NDEwfDA&ixlib=rb-4.1.0&q=85",
  opera: "https://thumb.wikimedia.org/wikipedia/commons/thumb/9/94/Kopenhagen_%28DK%29%2C_Hafen_und_Opernhaus_--_2017_--_1713.jpg/1280px-Kopenhagen_%28DK%29%2C_Hafen_und_Opernhaus_--_2017_--_1713.jpg",
  copenhill: "https://images.pexels.com/photos/18947202/pexels-photo-18947202.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  reffen: "/planner/46fc82545ebe.webp",
  harbourbath: "/planner/6982e5c5ee70.webp",
  havnevigen: "https://images.pexels.com/photos/30070113/pexels-photo-30070113.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  andersen: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/8ee8j913_2026-08-24%2020_48_38.622%2B0200.webp",
  jaegersborggade: "https://images.pexels.com/photos/36533142/pexels-photo-36533142.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  assistens: "/planner/b989d2ac1cde.webp",
  grundtvigs: "/planner/e41d03e4f74b.webp",
  blaagaards: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/4d/Bl%C3%A5g%C3%A5rdsgade_%28N%C3%B8rrebro%29.jpg/1280px-Bl%C3%A5g%C3%A5rdsgade_%28N%C3%B8rrebro%29.jpg",
  diamondpizza: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/wu1hx5pm_Screenshot_20261006_003936_Instagram.jpg",
  istedgade: "https://images.pexels.com/photos/38354159/pexels-photo-38354159.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  enghaveplads: "https://upload.wikimedia.org/wikipedia/commons/thumb/6/6d/Enghave_Plads_05.jpg/1280px-Enghave_Plads_05.jpg",
  koedbyen: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/dizcf0o8_20260906_170916.webp",
  "ismageriet-kodbyen": "/planner/62ab2b9ed816.webp",
  "ismageriet-refshaleoen": "/planner/62ab2b9ed816.webp",
  zoo: "https://images.unsplash.com/photo-1597953601389-5f66416e47c4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxDb3BlbmhhZ2VuJTIwWm9vJTIwYW5pbWFsc3xlbnwwfHx8fDE3ODcwNTc0MzV8MA&ixlib=rb-4.1.0&q=85",
  frederiksberghave: "/planner/9815d8fba0e3.webp",
  cisternerne: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a1/Cisternerne_under_S%C3%B8ndermarken.jpg/1280px-Cisternerne_under_S%C3%B8ndermarken.jpg",
  smk: "https://thumb.wikimedia.org/wikipedia/commons/thumb/c/c4/Statens_Museum_for_Kunst%2C_Copenhagen_%2836361350506%29.jpg/1280px-Statens_Museum_for_Kunst%2C_Copenhagen_%2836361350506%29.jpg",
  nationalmuseet: "https://upload.wikimedia.org/wikipedia/commons/6/66/Nationalmuseet_kbh.jpg",
  kbhmuseum: "https://thumb.wikimedia.org/wikipedia/commons/thumb/1/10/K%C3%B8benhavns_Museum_01.jpg/1280px-K%C3%B8benhavns_Museum_01.jpg",
  dac: "https://thumb.wikimedia.org/wikipedia/commons/thumb/e/e5/BLOX_Copenhagen_2019_08_05.jpg/1280px-BLOX_Copenhagen_2019_08_05.jpg",
  sortdiamant: "https://thumb.wikimedia.org/wikipedia/commons/thumb/3/3b/The_Black_Diamond_Royal_Danish_Library_Copenhagen_Slotsholmen_2014_01.jpg/1280px-The_Black_Diamond_Royal_Danish_Library_Copenhagen_Slotsholmen_2014_01.jpg",
  botaniskhave: "https://thumb.wikimedia.org/wikipedia/commons/thumb/8/86/2023-04-19_Palm_House_Copenhagen_01.jpg/1280px-2023-04-19_Palm_House_Copenhagen_01.jpg",
  superkilen: "https://thumb.wikimedia.org/wikipedia/commons/thumb/a/af/Superkilen_hill-top_view.jpg/1280px-Superkilen_hill-top_view.jpg",
  goboat: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/i5tzj4n6_2026-09-24%2011_54_48.360%2B0200.webp",
  carlsberg: "https://thumb.wikimedia.org/wikipedia/commons/thumb/a/aa/Elefantporten_%28Carlsberg%29.jpg/1280px-Elefantporten_%28Carlsberg%29.jpg",
  carlsbergbyen: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/d83a442d29de224e_20261006_182619.webp",
  experimentarium: "https://thumb.wikimedia.org/wikipedia/commons/thumb/1/19/Experimentarium%2C_Hellerup.jpg/1280px-Experimentarium%2C_Hellerup.jpg",
  cc: "https://thumb.wikimedia.org/wikipedia/commons/thumb/8/8b/Copenhagen_Contemporary_Refshale%C3%B8en.jpg/1280px-Copenhagen_Contemporary_Refshale%C3%B8en.jpg",
  frederiksborg: "https://thumb.wikimedia.org/wikipedia/commons/thumb/3/32/Frederiksborg_Castle%2C_Hiller%C3%B8d.jpg/1280px-Frederiksborg_Castle%2C_Hiller%C3%B8d.jpg",
  bispebjerg: "/planner/88da3e4a56dd.webp",
  "xmas-kongensnytorv": "/planner/eb903574c6e6.webp",
  "xmas-radhuspladsen": "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/rn2xhl0i_2026-08-19%2000_54_54.578%2B0200.webp",
  "xmas-gammeltorv": "/christmas/reindeer.webp",
  "xmas-hojbro": "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/357ef4775a619a15_2026-10-14%2014_41_54.868%2B0300.jpg",
  hviids: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/9bsmgtsu_2026-09-28%2022_11_04.726%2B0200.webp",
  detrenebrod: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/jn21spnj_2026-09-28%2022_20_25.023%2B0200.webp",
  andersenmaillard: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/01zbyst0_2026-09-28%2022_18_28.336%2B0200.webp",
  eriks: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/vmxj1k7o_20260926_181621.webp",
  dangleterre: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/78kl31ob_2026-09-03%2000_47_45.805%2B0200.webp",
  thorvaldsens: "https://images.pexels.com/photos/4788183/pexels-photo-4788183.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  christiansborg: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a3/Christiansborg_Slot%2C_Copenhagen.JPG/1280px-Christiansborg_Slot%2C_Copenhagen.JPG",
  glyptoteket: "https://images.unsplash.com/photo-1534955702244-4957157f5c2b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxOeSUyMENhcmxzYmVyZyUyMEdseXB0b3RlayUyMHdpbnRlciUyMGdhcmRlbiUyMENvcGVuaGFnZW58ZW58MHx8fHwxNzg3MzE1MDQ4fDA&ixlib=rb-4.1.0&q=85",
  designmuseum: "https://images.unsplash.com/photo-1727968414909-c87ceb32c9dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxEZXNpZ25tdXNldW0lMjBEYW5tYXJrJTIwaW50ZXJpb3IlMjBDb3BlbmhhZ2VufGVufDB8fHx8MTc4NzA1ODI4MHww&ixlib=rb-4.1.0&q=85",
  "swim-sandkaj": "https://images.unsplash.com/photo-1685945720469-e4b8c7f367bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxDb3BlbmhhZ2VuJTIwaGFyYm91ciUyMGJhdGglMjBzd2ltbWluZyUyMHN1bW1lcnxlbnwwfHx8fDE3ODcwNTc0NzZ8MA&ixlib=rb-4.1.0&q=85",
  "swim-papiroen": "/planner/26a8a5811698.webp",
  "swim-amagerstrand": "https://images.pexels.com/photos/30070113/pexels-photo-30070113.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  blaaplanet: "https://thumb.wikimedia.org/wikipedia/commons/thumb/1/18/Den_Bl%C3%A5_Planet%2C_Kastrup_%28East_Face_-_01%29.jpg/1280px-Den_Bl%C3%A5_Planet%2C_Kastrup_%28East_Face_-_01%29.jpg",
  bakken: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/z5yd1kh3_20261003_172047.webp",
  louisiana: "https://images.pexels.com/photos/25473965/pexels-photo-25473965.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  helsingor: "https://images.unsplash.com/photo-1748814670185-ae3f3b8fee7d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxLcm9uYm9yZyUyMENhc3RsZSUyMEhlbHNpbmdvcnxlbnwwfHx8fDE3ODcwNTc0MzV8MA&ixlib=rb-4.1.0&q=85",
  malmo: "https://images.unsplash.com/photo-1509356843151-3e7d96241e11?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxNYWxtbyUyMFN3ZWRlbiUyMFR1cm5pbmclMjBUb3JzbyUyMHNreWxpbmV8ZW58MHx8fHwxNzg3MDU3NDM1fDA&ixlib=rb-4.1.0&q=85",
  dragor: "https://images.pexels.com/photos/38521805/pexels-photo-38521805.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

// Neutral fallback (ads/partners and any future spot without a photo)
const SPOT_IMAGE_FALLBACK = "https://images.unsplash.com/photo-1722355004719-f18da8bcd6f2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxjb3BlbmhhZ2VuJTIwc3RyZWV0fGVufDB8fHx8MTc4NzA1NzI2NHww&ixlib=rb-4.1.0&q=85";

// Downsize Unsplash photos to the size actually shown. The planner grid and
// teaser render small thumbnails, but the raw URLs ask for full-resolution
// images (often 2000px+). Requesting w=640 + auto=format (WebP/AVIF) cuts the
// transfer size dramatically. Custom-CDN (.webp) assets are left untouched.
function sizedUrl(url, w = 640) {
  if (typeof url !== "string" || !url.includes("images.unsplash.com")) return url;
  const [base, query = ""] = url.split("?");
  const params = new URLSearchParams(query);
  params.set("w", String(w));
  params.set("q", "70");
  params.set("auto", "format");
  params.set("fit", "crop");
  return `${base}?${params.toString()}`;
}

export const spotImg = (id, width = 640) =>
  sizedUrl(SPOT_IMAGES[id] || SPOT_IMAGE_FALLBACK, width);

// "any" means no season chosen — seasonal event spots (the Christmas markets)
// stay out of the picker and out of generated plans until Nov/Dec is picked.
export const isAvailable = (spot, month) =>
  month == null || (month === "any" ? !spot.event : spot.seasons.includes(Number(month)));

const MONTH_ABBR = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Short availability badge for a spot chip, e.g. "Jun–Aug", "Feb only"
export const seasonHint = (spot) => {
  if (spot.hint) return spot.hint;
  const m = spot.seasons;
  if (!m || m.length >= 12) return null;
  const s = [...m].sort((a, b) => a - b);
  return s.length === 1
    ? `${MONTH_ABBR[s[0]]} only`
    : `${MONTH_ABBR[s[0]]}–${MONTH_ABBR[s[s.length - 1]]}`;
};

// Opening-date notes for any spots in a plan that are season-limited (deduped by text)
export const openingNotes = (spots) => {
  const seen = new Set();
  const out = [];
  (spots || []).forEach((s) => {
    if (!s || !s.open || seen.has(s.open)) return;
    seen.add(s.open);
    out.push({ name: s.name, open: s.open, anon: !!s.openAnon, tip: s.tip });
  });
  return out;
};

// Central core = Indre By + the harbour front (Amalienborg / Nyhavn) — one walkable zone
const ZONE_OF = {
  "indre-by": "central",
  harbour: "central",
  osterbro: "osterbro",
  christianshavn: "christianshavn",
  refshaleoen: "refshaleoen",
  "islands-brygge": "islands-brygge",
  norrebro: "norrebro",
  nordvest: "nordvest",
  vesterbro: "vesterbro",
  frederiksberg: "frederiksberg",
  amager: "amager",
  daytrip: "daytrip",
};

const ZONE_ORDER = [
  "central",
  "vesterbro",
  "frederiksberg",
  "osterbro",
  "christianshavn",
  "refshaleoen",
  "islands-brygge",
  "amager",
  "norrebro",
  "nordvest",
  "daytrip",
];

const ZONE_LABEL = {
  central: "Indre By",
  vesterbro: "Vesterbro",
  frederiksberg: "Frederiksberg",
  osterbro: "Østerbro",
  christianshavn: "Christianshavn",
  refshaleoen: "Refshaleøen",
  "islands-brygge": "Islands Brygge",
  amager: "Amager",
  norrebro: "Nørrebro",
  nordvest: "Nordvest",
  daytrip: "Out of town",
};

const zoneRank = (z) => {
  const i = ZONE_ORDER.indexOf(z);
  return i < 0 ? 99 : i;
};

const ANCHOR_MIN = 3; // spots this long earn a lighter day of their own (Tivoli, day trips)
export const DAY_MAX = 8; // merging two day-clusters past ~8h is discouraged — better
// to pair a light group with another light group (e.g. Reffen + Christianshavn)
// than fuse two full areas into one overstuffed day
// Bakeries & ice-cream stops belong to their area's day — never rebalanced away.
// Nyboder too: it sits 5 min from Amalienborg/Marmorkirken, so it must stay on
// the harbour day whenever there is one — never exiled to an Indre By or
// Vesterbro day just because that day is lighter.
const AREA_BOUND = new Set([
  "sanktpeders",
  "laglace",
  "juno",
  "bosses",
  "andersen",
  "ismageriet",
  "ismageriet-kodbyen",
  "ismageriet-refshaleoen",
  "nyboder",
]);

// A "unit" is one or more spots that must stay together (e.g. Torvehallerne +
// its smørrebrød bite). Clustering works on units; a "day" is an array of spots.
const isAnchorSpot = (s) => s.cluster !== "daytrip" && s.dur >= ANCHOR_MIN;
const isDaytripSpot = (s) => s.cluster === "daytrip";
// Tivoli says "3–5h" on the card, but it secretly counts as 3h when packing —
// it caps an evening, and charging the full 4h lets a 7–9h day stack it on top.
const effDur = (s) => (s.id === "tivoli" ? 3 : s.dur);
const spotsHours = (spots) => spots.reduce((t, s) => t + effDur(s), 0);
const centroid = (spots) => [
  spots.reduce((t, s) => t + s.coords[0], 0) / spots.length,
  spots.reduce((t, s) => t + s.coords[1], 0) / spots.length,
];

// Spots that belong right next to each other and should never be split across days.
// Bakeries & ice-cream stops are coupled to their area's companion spot so they
// always share the area's day (e.g. Sankt Peders with the Torvehallerne day).
const COUPLED = [
  ["nyhavn", "canaltour"],
  ["koedbyen", "ismageriet"],
  ["amalienborg", "marmorkirken"], // literally 100m apart — always back to back
  ["nyboder", "amalienborg"], // Nyboder begins 5 min from the palace — same royal-quarter walk, same day
  // the royal waterfront is ONE continuous walk — Kastellet & the Little
  // Mermaid are 10–15 min up the quay from Amalienborg, Nyhavn 5 min below it:
  // never split across days
  ["littlemermaid", "amalienborg"],
  ["amalienborg", "nyhavn"],
  ["nyboder", "krusemyntegade"], // the rainbow rowhouses sit between Nyboder and Kastellet's ramparts — same walk
  ["istedgade", "enghaveplads"], // the square is where Istedgade ends — same Vesterbro wander
  ["broens", "swim-papiroen"], // Papirøen's quay is a 2-min walk from Broens' street food — always together
  ["rundetaarn", "rosenborg"], // the Round Tower sits by Rosenborg & the Kongens Have entry
  // the Slotsholmen pocket — Christiansborg, Thorvaldsens, the Library Garden
  // and the Black Diamond are all within ~300m: one visit, one day
  ["christiansborg", "thorvaldsens"],
  ["christiansborg", "royallibrarygarden"],
  ["christiansborg", "sortdiamant"],
  ["sortdiamant", "royallibrarygarden"], // the Black Diamond opens straight onto the Library Garden — a few steps apart
  ["sortdiamant", "thorvaldsens"], // both on Slotsholmen, a short walk from the Diamond
  ["sanktpeders", "torvehallerne"],
  ["sanktpeders", "latinquarter"], // the bakery sits IN the Latin Quarter — always together
  ["graabroedretorv", "latinquarter"], // the square is part of the same old-town pocket
  ["graabroedretorv", "sanktpeders"], // ...and a 4-min walk from the bakery — keep the trio back to back
  ["laglace", "stroget"],
  ["juno", "bosses"],
  ["ismageriet", "kartoffelraekkerne"],
  ["andersen", "harbourbath"],
  ["reffen", "ismageriet-refshaleoen"],
  ["carlsberg", "carlsbergbyen"], // the stroll IS the brewery district — always the same day as the brewhouse
];

// Companion taps: picking either spot in a pair offers the other as a
// one-tap add (a toast with "+ Add") — pairs that only make sense together.
export const COMPANION_PAIRS = [
  ["carlsberg", "carlsbergbyen"], // the brewhouse & the district around it
  ["nyboder", "krusemyntegade"], // the two rowhouse streets
  ["amalienborg", "marmorkirken"], // palace square & the Marble Church dome
  ["nyhavn", "canaltour"], // the harbour & the boat that sails from it
  ["canalstroll", "amagergade"], // the Christianshavn canal walk & its prettiest street
  ["kbhmuseum", "magstraede"], // the city museum & the old cobbled lane a 3-min walk away
  ["sortdiamant", "royallibrarygarden"], // the Black Diamond & the Library Garden right beside it
  ["harbourbath", "andersen"], // Islands Brygge dip & the bakery behind the harbourfront
  ["superkilen", "jaegersborggade"], // Nørrebro's two wanders
  ["blaagaards", "diamondpizza"], // the Nørrebro street & its pizza slice
  ["assistens", "blaagaards"], // the cemetery-park & the nearby street
  ["bispebjerg", "grundtvigs"], // the cemetery cherries & the church
  ["brumleby", "olufsgade"], // Østerbro's two quiet pockets
  ["olufsgade", "bosses"], // the street & its bakery
  ["brumleby", "bosses"], // the terraces & their bakery
  ["bosses", "juno"], // the Østerbro bakery crawl
  ["marmorkirken", "nyboder"], // the Marble Church & the rowhouse quarter behind it
  ["canalstroll", "christiania"], // the Christianshavn canal walk & Freetown Christiania
  ["royallibrarygarden", "christiansborg"], // the garden & the palace on Slotsholmen
  ["jaegersborggade", "assistens"], // the Nørrebro street & the cemetery-park beside it
  ["smk", "rosenborg"], // the National Gallery & the castle across the park
  ["rosenborg", "botaniskhave"], // the castle gardens & the Botanical Garden next door
  ["designmuseum", "littlemermaid"], // the design museum & Kastellet/the Mermaid up the road
  ["reffen", "lille"], // Refshaleøen street food & its bakery
  ["reffen", "cc"], // Reffen & Copenhagen Contemporary out on Refshaleøen
  ["cc", "lille"], // the art halls & the bakery nearby
  ["istedgade", "enghaveplads"], // the Vesterbro street & the square it ends at
  ["enghaveplads", "carlsbergbyen"], // the square & the Carlsberg district walk
  ["enghaveplads", "brod"], // the square & Bageriet BRØD right on it
  ["carlsbergbyen", "kobenhavnsbageri"], // the Carlsberg Byen stroll & its bakery
  ["experimentarium", "nordhavn"], // the science centre & the Nordhavn waterfront
];

// All companions for a spot (a spot can sit in several pairs — the toast
// offers the first one not already picked)
export const companionsOf = (id) =>
  COMPANION_PAIRS.filter((p) => p.includes(id)).map((p) => p.find((x) => x !== id));

// After clustering, spread the load: peel movable stops off the busiest day onto
// the lightest day when they sit close enough to belong there. Keeps day trips and
// big anchors (Tivoli) on their own, and never splits coupled stops or pinned
// spots (e.g. the smørrebrød bite pinned inside Torvehallerne).
// A heavy day with nothing sensible to give is parked ("stuck") and the loop
// retries with the next-heaviest day — otherwise one incompatible busy day
// (e.g. the Tivoli day) would stop a Christianshavn pair from joining
// Christiania on the lighter harbour day. Each stop moves at most once, so
// nothing can ping-pong between days.
function rebalanceDays(clusters, extraPairs = []) {
  const pairMap = new Map();
  [...COUPLED, ...extraPairs].forEach(([x, y]) => {
    pairMap.set(x, [...(pairMap.get(x) || []), y]);
    pairMap.set(y, [...(pairMap.get(y) || []), x]);
  });
  const movedIds = new Set(); // a stop moves at most once per rebalance
  const stuck = new Set(); // heavy days found to have nothing sensible to give
  const movable = (s) =>
    !isDaytripSpot(s) && !isAnchorSpot(s) && !s.pinned && !AREA_BOUND.has(s.id) && !movedIds.has(s.id);
  // balance on travel-inclusive hours — the number visitors actually see
  const H = (c) => dayHoursWithTravel(c);
  // the trip's peak day (day trips excluded) — the number to bring down
  const peakH = () => Math.max(...clusters.map((c) => (c.some(isDaytripSpot) ? 0 : H(c))));
  const BALANCED = 2; // stop once the trip's peak and the lightest day are within ~2h
  const NEAR_KM = 3.5; // only move a stop to a non-empty day if it's this close
  let guard = 0;
  while (guard++ < 80) {
    // heaviest day that still has something we can move (skip day-trip days
    // and parked days)
    let hi = -1;
    let hiH = -Infinity;
    clusters.forEach((c, i) => {
      if (stuck.has(i) || c.some(isDaytripSpot)) return;
      if (c.some(movable) && H(c) > hiH) {
        hiH = H(c);
        hi = i;
      }
    });
    if (hi < 0) break;
    // lightest day that can receive stops (skip day-trip days; anchor days
    // like Tivoli may receive stops while they're still light)
    let lo = -1;
    let loH = Infinity;
    clusters.forEach((c, i) => {
      if (i === hi || c.some(isDaytripSpot)) return;
      if (c.some(isAnchorSpot) && H(c) >= 4.5) return;
      if (H(c) < loH) {
        loH = H(c);
        lo = i;
      }
    });
    if (lo < 0 || peakH() - loH <= BALANCED) break;

    const targetRef = clusters[lo].length ? centroid(clusters[lo]) : null;
    const heavyCentre = centroid(clusters[hi]);
    // hours a candidate move carries (including its coupled/pinned partners)
    const moveHoursOf = (s) => {
      let h = s.dur;
      (pairMap.get(s.id) || []).forEach((p) => {
        const px = clusters[hi].find((x) => x.id === p);
        if (px) h += px.dur;
      });
      return h;
    };
    // pick the stop to move: nearest to the light day, or (if it's empty) the
    // biggest outlier from the heavy day's centre so we seed a coherent new day
    let pick = null;
    let pd = Infinity;
    clusters[hi].forEach((s, idx) => {
      if (!movable(s)) return;
      // keep areas together: only peel a stop onto a day that already hosts
      // its area (or a strong-bond neighbour like Refshaleøen↔Christianshavn,
      // or an empty day we're seeding) — never onto a foreign day just because
      // it's geographically near (e.g. harbour → Christianshavn)
      if (
        clusters[lo].length &&
        !clusters[lo].some(
          (x) => x.cluster === s.cluster || STRONG_BOND.has(areaKey(x.cluster, s.cluster))
        )
      )
        return;
      // and never push the receiving day past the ~7h ceiling
      if (clusters[lo].length && loH + moveHoursOf(s) > DAY_MAX) return;
      // nor land a 4th museum on it
      if (isMuseum(s) && museumCount(clusters[lo]) >= 3) return;
      const d = targetRef ? roadKmCoords(s.coords, targetRef) : -roadKmCoords(s.coords, heavyCentre);
      if (d < pd) {
        pd = d;
        pick = { s, idx };
      }
    });
    // Rescue: a light day may adopt up to two stops that share ONE area of a
    // busy day (they're close to each other by definition) — a lend when the
    // area keeps a presence on the busy day, or a wholesale relocation when the
    // whole area fits better there (e.g. the Christianshavn pair moving onto a
    // light Tivoli day). Never splits coupled pairs that are actually on the
    // day, and never spreads an area across more days.
    let rescueIds = null;
    if (!pick && targetRef && loH < 4.5 && hiH > 6.5) {
      const byClu = new Map();
      clusters[hi].forEach((s) => {
        if (!movable(s)) return;
        // only treat a spot as coupled when its partner is actually on this day
        const partners = pairMap.get(s.id) || [];
        if (partners.some((p) => clusters[hi].some((x) => x.id === p))) return;
        if (!byClu.has(s.cluster)) byClu.set(s.cluster, []);
        byClu.get(s.cluster).push(s);
      });
      let bestSet = null;
      let bestScore = Infinity;
      byClu.forEach((arr, clu) => {
        const totalAll = clusters.reduce((t, c) => t + c.filter((x) => x.cluster === clu).length, 0);
        const hereAll = clusters[hi].filter((x) => x.cluster === clu).length;
        if (totalAll !== hereAll) return; // area already spans multiple days
        const daysWith = clusters.filter((c) => c.some((x) => x.cluster === clu)).length;
        if (daysWith >= 2) return; // never spread an area further
        const sets = [];
        for (let i = 0; i < arr.length; i++)
          for (let j = i + 1; j < arr.length; j++) sets.push([arr[i], arr[j]]);
        arr.forEach((s) => sets.push([s]));
        sets.forEach((set) => {
          if (loH + set.reduce((t, s) => t + s.dur, 0) > DAY_MAX) return; // respect the ceiling
          if (museumCount(clusters[lo]) + museumCount(set) > 3) return; // museum fatigue cap
          const d = Math.min(...set.map((s) => roadKmCoords(s.coords, targetRef)));
          const score = d - (set.length - 1) * 1.5; // prefer moving a close pair over a single
          if (score < bestScore) {
            bestScore = score;
            bestSet = set;
          }
        });
      });
      if (bestSet) rescueIds = new Set(bestSet.map((s) => s.id));
    }
    if (!pick && !rescueIds) {
      stuck.add(hi);
      continue;
    }
    if (pick && targetRef && pd > NEAR_KM) {
      stuck.add(hi); // nothing close enough to move sensibly
      continue;
    }

    // move the stop (and its coupled/pinned partners, if on the same day)
    const moveIds = rescueIds || new Set([pick.s.id]);
    (pick ? pairMap.get(pick.s.id) || [] : []).forEach((partner) => {
      if (clusters[hi].some((x) => x.id === partner)) moveIds.add(partner);
    });
    const moveH = clusters[hi].filter((x) => moveIds.has(x.id)).reduce((t, x) => t + x.dur, 0);
    // only move when it lowers the trip's peak day
    if (Math.max(hiH - moveH, loH + moveH) >= peakH()) {
      stuck.add(hi);
      continue;
    }

    const moved = clusters[hi].filter((x) => moveIds.has(x.id));
    clusters[hi] = clusters[hi].filter((x) => !moveIds.has(x.id));
    clusters[lo] = clusters[lo].concat(moved);
    moved.forEach((x) => movedIds.add(x.id));
    stuck.clear(); // the landscape changed — parked days may have options again
  }
  return clusters;
}

// Area pairs that naturally belong to the same day when merges are forced.
export const AREA_AFFINITY = new Set([
  "christianshavn|refshaleoen", // same side of the harbour, itinerary day-3 flow
  "nordvest|norrebro", // Nørrebro runs straight into Nordvest
  "frederiksberg|vesterbro", // neighbouring districts west of the centre
  "islands-brygge|vesterbro", // just across Langebro
  "christianshavn|harbour", // across Inderhavnsbroen from Nyhavn
  "indre-by|vesterbro", // the centre flows straight into Vesterbro
]);
export const areaKey = (a, b) => [a, b].sort().join("|");

// Christianshavn & Refshaleøen are effectively one trip across the harbour —
// the bond is strong enough to beat pure distance and even the busy-day cap.
const STRONG_BOND = new Set(["christianshavn|refshaleoen"]);

// Museum fatigue rule: max 2 museums a day is ideal, 3 tolerable when the
// area demands it — 4 on one day is a fail even when they're neighbours.
const isMuseum = (s) => spotCat(s.id) === "museum";
const museumCount = (spots) => spots.filter(isMuseum).length;
const MUSEUM_PENALTY = (n) => (n <= 2 ? 0 : n === 3 ? 4 : 100);

// Cost of putting two day-clusters together — lower is better. Driven by real
// (bridge-aware) road distance so stops across the harbour stay on separate days.
function mergeCost(a, b) {
  let cost = roadKmCoords(centroid(a), centroid(b));
  const ca = new Set(a.map((s) => s.cluster));
  const cb = new Set(b.map((s) => s.cluster));
  let strongBond = false;
  let crossPenalty = 3;
  ca.forEach((x) =>
    cb.forEach((y) => {
      const k = areaKey(x, y);
      if (STRONG_BOND.has(k)) strongBond = true;
      else if (AREA_AFFINITY.has(k)) crossPenalty = Math.min(crossPenalty, 0.5);
    })
  );
  // same area, same day — strongly prefer merging clusters that already share
  // an area, so e.g. all the harbour stops end up together. A strong-bond pair
  // (Christianshavn↔Refshaleøen) even gets a pull beyond pure distance.
  if (!b.some((s) => ca.has(s.cluster))) cost += strongBond ? -1.5 : crossPenalty;
  if (a.some(isAnchorSpot) && b.some(isAnchorSpot)) cost += 1000; // two big anchors → split
  const dtA = a.some(isDaytripSpot);
  const dtB = b.some(isDaytripSpot);
  if (dtA || dtB) {
    // a day trip eats the whole day — keep it on its own
    const others =
      a.filter((s) => !isDaytripSpot(s)).length + b.filter((s) => !isDaytripSpot(s)).length;
    if (others > 0) cost += 2000;
    if (dtA && dtB) cost += 2000;
  }
  const hrs = spotsHours(a) + spotsHours(b);
  // strong-bond days may run busy; other merges hit the cap hard
  if (hrs > DAY_MAX) cost += strongBond ? (hrs - DAY_MAX) * 10 : 100 + (hrs - DAY_MAX) * 25; // discourage overstuffed days
  cost += MUSEUM_PENALTY(museumCount(a) + museumCount(b));
  return cost;
}

// Reunite split areas: if one area's stops landed on two different days and
// the day with room can absorb the rest without busting the ~8h ceiling,
// move them over — nobody wants to do the same neighbourhood twice (e.g. the
// Our Saviour's spire on day 1 while Christiania waits on day 4).
function reuniteAreas(clusters) {
  const H = (c) => dayHoursWithTravel(c);
  // big spread-out areas are naturally visited in pieces — only compact
  // neighbourhoods must stay on one day
  const NEVER_REUNITE = new Set(["indre-by", "harbour", "amager", "daytrip"]);
  let guard = 0;
  let changed = true;
  while (changed && guard++ < 10) {
    changed = false;
    const byCluster = new Map();
    clusters.forEach((c, di) =>
      c.forEach((s) => {
        if (!byCluster.has(s.cluster)) byCluster.set(s.cluster, []);
        byCluster.get(s.cluster).push({ s, di });
      })
    );
    for (const [clu, entries] of byCluster) {
      if (NEVER_REUNITE.has(clu)) continue;
      const dayIdxs = [...new Set(entries.map((e) => e.di))];
      if (dayIdxs.length < 2) continue;
      // host = the day holding the most of this area (ties: the lighter day)
      const ranked = dayIdxs
        .map((di) => ({ di, n: entries.filter((e) => e.di === di).length, h: H(clusters[di]) }))
        .sort((a, b) => b.n - a.n || a.h - b.h);
      const host = ranked[0].di;
      const movers = entries.filter((e) => e.di !== host && !e.s.pinned && !isAnchorSpot(e.s) && !isDaytripSpot(e.s));
      if (!movers.length) continue;
      // small 1–2 stop days may hand their area over wholesale (becoming a
      // genuinely free day); a richer day never gets hollowed out as a
      // side-effect of tidying areas
      const giverDays = [...new Set(movers.map((e) => e.di))];
      // a richer giver day is usually MORE coherent once a foreign stop leaves
      // (e.g. Jægersborggade off the Reffen day) — only block a reunion that
      // would hollow a day down to a single orphan stop
      if (
        giverDays.some(
          (di) => clusters[di].length > 2 && clusters[di].length - movers.filter((e) => e.di === di).length === 1
        )
      )
        continue;
      // reunite when the host has room — a packed ~9h same-area day still
      // beats doing the same neighbourhood twice
      const after = H([...clusters[host], ...movers.map((e) => e.s)]);
      if (after > DAY_MAX + 1) continue;
      const ids = new Set(movers.map((e) => e.s.id));
      clusters.forEach((c, di) => {
        if (di !== host) clusters[di] = c.filter((s) => !ids.has(s.id));
      });
      clusters[host] = clusters[host].concat(movers.map((e) => e.s));
      changed = true;
      break; // re-evaluate from scratch after each reunion
    }
  }
  return clusters;
}

// Agglomerative merge: start with each unit on its own, merge the cheapest pair
// until we're down to `days` clusters. Keeps nearby stops together.
function clusterDays(units, days, extraPairs = []) {
  let clusters = units.map((u) => [...u]);
  while (clusters.length > days) {
    let bi = -1;
    let bj = -1;
    let best = Infinity;
    for (let i = 0; i < clusters.length; i++) {
      for (let j = i + 1; j < clusters.length; j++) {
        const c = mergeCost(clusters[i], clusters[j]);
        if (c < best) {
          best = c;
          bi = i;
          bj = j;
        }
      }
    }
    if (bi < 0) break;
    clusters[bi] = clusters[bi].concat(clusters[bj]);
    clusters.splice(bj, 1);
  }
  while (clusters.length < days) clusters.push([]);
  const balanced = reuniteAreas(rebalanceDays(clusters, extraPairs));
  // Sort AFTER rebalancing so the order reflects the final days: central days
  // first (a natural centre-outward trip flow), day trips last (users can still
  // drag to reorder).
  const CORE = [55.6805, 12.585];
  balanced.sort((a, b) => {
    if (!a.length) return 1;
    if (!b.length) return -1;
    const da = a.some(isDaytripSpot) ? 1 : 0;
    const db = b.some(isDaytripSpot) ? 1 : 0;
    if (da !== db) return da - db;
    return roadKmCoords(centroid(a), CORE) - roadKmCoords(centroid(b), CORE);
  });
  // Nyhavn is the postcard classic — it can fall on any day, just never the
  // LAST day of the trip (only pulled forward if it would land there).
  const lastIdx = balanced.reduce((acc, c, i) => (c.length ? i : acc), -1);
  const ni = balanced.findIndex((c) => c.some((s) => s.id === "nyhavn"));
  if (ni > 0 && ni === lastIdx) {
    const [ny] = balanced.splice(ni, 1);
    balanced.unshift(ny);
  }
  return balanced;
}

// Order a day as a sensible walking route (no doubling back): nearest-neighbour
// path, improved with 2-opt to minimise total distance, then oriented by
// time-of-day. Day trips go first (alone); big anchors (Tivoli) go last.
// A route is judged on walking distance PLUS a penalty for time-of-day
// inversions (e.g. the Reffen street-food lunch scheduled before the CopenHill
// hike). Inversions across time buckets (a lunch stop before a morning one)
// cost firmly; within one bucket it's just a gentle nudge so geography can win
// (e.g. Torvehallerne right after Rundetårn, the Glyptoteket last by Tivoli).
const ORDER_PENALTY = 1.2; // km-equivalent cost per order-step, across buckets
const SAME_BUCKET_PENALTY = 0.6; // per order-step within one time bucket
// actual meal stops — a meal must never open the day ahead of a sight
const MEAL = new Set(["smorrebrod", "gasolinegrill", "reffen", "koedbyen", "broens", "diamondpizza"]);
const orderPenalty = (arr) => {
  let p = 0;
  for (let i = 0; i < arr.length; i++)
    for (let j = i + 1; j < arr.length; j++) {
      const gap = (arr[i].order ?? 3) - (arr[j].order ?? 3);
      if (gap <= 0) continue;
      const sameBucket = orderBucket(arr[i]) === orderBucket(arr[j]);
      const mealJump = MEAL.has(arr[i].id) && !MEAL.has(arr[j].id);
      p += gap * (sameBucket && !mealJump ? SAME_BUCKET_PENALTY : ORDER_PENALTY);
    }
  return p;
};
const pathLen = (route) => {
  let t = 0;
  for (let i = 0; i < route.length - 1; i++) t += roadKmCoords(route[i].coords, route[i + 1].coords);
  t += orderPenalty(route);
  return t;
};
const nnFrom = (spots) => {
  const pool = spots.slice().sort((a, b) => (a.order ?? 3) - (b.order ?? 3));
  const route = [pool.shift()];
  while (pool.length) {
    let bi = 0;
    let best = Infinity;
    pool.forEach((s, i) => {
      const d = roadKmCoords(route[route.length - 1].coords, s.coords);
      if (d < best) {
        best = d;
        bi = i;
      }
    });
    route.push(pool.splice(bi, 1)[0]);
  }
  return route;
};
const twoOpt = (start) => {
  let route = start;
  let improved = true;
  while (improved) {
    improved = false;
    for (let i = 0; i < route.length - 1; i++) {
      for (let k = i + 1; k < route.length; k++) {
        const cand = route.slice(0, i).concat(route.slice(i, k + 1).reverse(), route.slice(k + 1));
        if (pathLen(cand) < pathLen(route) - 1e-9) {
          route = cand;
          improved = true;
        }
      }
    }
  }
  return route;
};
// Order a day's walkable stops. Stops are bucketed by time-of-day first
// (morning order<2, lunch 2.x, afternoon 3.x, evening 4+) so a lunch stop like
// smørrebrød can never land before a morning bakery or stroll — then each
// bucket is optimised for walking distance and stitched in time order.
const orderBucket = (s) => {
  const o = s.order ?? 3;
  return o < 2 ? 0 : o < 3 ? 1 : o < 4 ? 2 : 3;
};
function optimizeOrder(spots, anchorCoord) {
  if (spots.length <= 1) return spots.slice();
  const CORE = [55.6805, 12.585];
  const hotel = TRIP.hotel && Array.isArray(TRIP.hotel.coords) ? TRIP.hotel.coords : null;
  // Optimise the whole day at once: walking distance PLUS time-of-day
  // penalties (a meal jumping ahead of morning stops costs kilometres), so
  // geography wins when it clearly should (Nyhavn → Broens are 5 min apart)
  // without a lunch stop ever opening the day ahead of the morning sights.
  const route =
    spots.length <= 2
      ? spots.slice().sort((a, b) => (a.order ?? 3) - (b.order ?? 3))
      : twoOpt(nnFrom(spots));
  if (route.length > 2) {
    const a = route[0].coords;
    const b = route[route.length - 1].coords;
    const rev = route.slice().reverse();
    const flipCost = orderPenalty(rev) - orderPenalty(route);
    if (anchorCoord) {
      // flow TOWARD the day's big anchor — end nearest it
      if (roadKmCoords(a, anchorCoord) + flipCost < roadKmCoords(b, anchorCoord)) return rev;
    } else if (hotel) {
      // a hotel day leaves from and returns to the door — start at the stop
      // nearest the hotel and loop back (even when the hotel is itself far out)
      if (flipCost <= 0 && roadKmCoords(b, hotel) < roadKmCoords(a, hotel)) return rev;
    } else if (flipCost <= 0 && roadKmCoords(a, CORE) < roadKmCoords(b, CORE)) {
      // start at the farthest-out stop and walk back toward the centre — so a
      // day ends near the core (and its evening spots) instead of trekking out
      // last — but only when flipping doesn't scramble the time-of-day flow
      return rev;
    }
  }
  return route;
}

// These spots must sit back to back within a day — Gråbrødretorv sits IN the
// Latin Quarter, and Sankt Peders is a 4-min walk off the square. Enforced by
// orderDayItems after the optimiser runs, and respected when a newcomer is
// slotted into an already-arranged day (nothing ever lands between a pair).
const ALWAYS_ADJACENT = [
  ["graabroedretorv", "latinquarter"],
  ["graabroedretorv", "sanktpeders"],
  ["enghaveplads", "brod"], // Bageriet BRØD sits right on Enghave Plads
  ["lille", "reffen"], // Lille Bakery & Reffen street food, next door on Refshaleøen
  ["istedgade", "betterbakery"], // Better Bakery on Helgolandsgade, just off Istedgade
];

// Morning openers: a covered food market makes a natural breakfast start, so
// it leads the day's walk when present (the rest flows outward from it).
const DAY_OPENERS = new Set(["torvehallerne"]);

// Far-out stops that must LEAD the day regardless of their time-of-day order.
// Den Blå Planet sits out by the airport (a Metro ride from town) and closes
// mid-afternoon — you go there first thing, then ride back to the centre.
// Without this the time-of-day optimiser (its order is 2.6) would bury it at
// the end of the day, stranding a closed aquarium after a day of walking.
const FRONT_LEADERS = new Set(["blaaplanet"]);

// Where a newcomer slots into an already-arranged day (suggestions, curated
// extensions): right after its nearest stop — but never BETWEEN
// an always-adjacent pair. GoBoat sails till sunset, so whenever it joins a
// day it's the last thing on the schedule (ahead of a trailing evening anchor
// like Tivoli) instead of splitting city-centre stops.
export const insertionIndexFor = (items, spot) => {
  if (spot.id === "goboat") {
    let end = items.length;
    while (end > 0 && isAnchorSpot(items[end - 1])) end--;
    return end;
  }
  let best = -1;
  let bestD = Infinity;
  items.forEach((s, k) => {
    const d = (s.coords[0] - spot.coords[0]) ** 2 + (s.coords[1] - spot.coords[1]) ** 2;
    if (d < bestD) {
      bestD = d;
      best = k;
    }
  });
  let at = best + 1;
  const splitsPair = ALWAYS_ADJACENT.some(
    ([x, y]) =>
      (items[best]?.id === x && items[best + 1]?.id === y) ||
      (items[best]?.id === y && items[best + 1]?.id === x)
  );
  if (splitsPair) at = best + 2;
  return at;
};

// Typical daily closing (24h) for spots where arriving too late is a real
// problem — museums, food halls, gated sights. Outdoor and late-night spots
// simply have no entry. (The Zoo keeps its own month-aware closesByMonth.)
const CLOSE_HOUR = {
  rosenborg: 17, thorvaldsens: 17, smk: 18, nationalmuseet: 17, kbhmuseum: 18,
  glyptoteket: 17, designmuseum: 17, dac: 18, cc: 18, cisternerne: 17,
  experimentarium: 17, christiansborg: 17, blaaplanet: 17, sortdiamant: 18,
  torvehallerne: 19, laglace: 18, sanktpeders: 17, juno: 18, bosses: 17,
  andersen: 17, smorrebrod: 17, broens: 22, reffen: 22, gasolinegrill: 22,
  detrenebrod: 17, buka: 18, andersenmaillard: 18, hart: 17,
  lille: 17, brod: 17, kobenhavnsbageri: 17,
  betterbakery: 17, alice: 18, eriks: 17,
  rundetaarn: 21, marmorkirken: 17, oursaviour: 19, botaniskhave: 18,
  copenhill: 20, tivoli: 23, bakken: 23, harbourbath: 20,
  bolsjefabrikken: 17, karenblixens: 17, blaagaards: 18, ismageriet: 20,
  diamondpizza: 21, lalandia: 20, carlsberg: 18, koedbyen: 22,
};

// Would a spot reached at ~startH (24h) still fit before it closes that month?
// Used to keep early-closing suggestions (museums, bakeries) OFF a day that
// already runs late — e.g. a late arrival where the Designmuseum would only be
// reachable at 18:15, past its close. Spots with no fixed closing always fit.
export const fitsBeforeClose = (spot, startH, month) => {
  if (!spot) return false;
  const c = spot.closesByMonth && month != null && month !== "any" ? spot.closesByMonth[month] ?? null : CLOSE_HOUR[spot.id] ?? null;
  return c == null || startH + spot.dur <= c;
};


// Weekly closing days (JS getDay(): 0 = Sunday, 1 = Monday …). `months`
// limits the closure to part of the year (Rosenborg opens daily in summer,
// Nationalmuseet only closes Mondays Nov–Mar). Used when the visitor gives
// exact dates: the planner keeps these off their closing day or flags them.
const CLOSED_ON = {
  rosenborg: { days: [1], exceptDates: [[[6, 27], [8, 30]]] }, // daily 27 Jun–30 Aug
  smk: { days: [1] },
  designmuseum: { days: [1] },
  thorvaldsens: { days: [1] },
  glyptoteket: { days: [1] },
  nationalmuseet: { days: [1], months: [11, 12, 1, 2, 3] },
  christiansborg: { days: [1] },
  cc: { days: [1] },
  cisternerne: { days: [1], months: [10, 11] },
  louisiana: { days: [1] },
  lille: { days: [1, 2] },
  sanktpeders: { days: [0] },
};
export const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

// Exact opening SEASONS for the spots whose months-list is only a rough guide.
// Used when the visitor gives dates: [month, day] ranges (a range may wrap the
// year), optional weekend-only stretches. Anything not listed falls back to
// the month-based `seasons`.
const MD = (m, d) => m * 100 + d;
const inRange = (md, [a, b]) => {
  const s = MD(...a);
  const e = MD(...b);
  return s <= e ? md >= s && md <= e : md >= s || md <= e;
};
const OPEN_DATES = {
  // summer season from 19 Mar to ~20 Sep, Halloween in October, Christmas mid-Nov → 3 Jan
  tivoli: { ranges: [[[3, 19], [9, 20]], [[10, 2], [10, 31]], [[11, 13], [1, 3]]] },
  reffen: { ranges: [[[3, 15], [10, 25]]] },
  "ismageriet-refshaleoen": { ranges: [[[3, 15], [10, 25]]] },
  // weekends from April, daily late May → mid Aug, weekends to mid Sep; then
  // Halloween (10–18 & 23–25 Oct) and Christmas (21 Nov → 20 Dec) — weekends only
  bakken: { ranges: [[[4, 1], [9, 15]], [[10, 10], [10, 18]], [[10, 23], [10, 25]], [[11, 21], [12, 20]]], weekendOnly: [[[4, 1], [5, 24]], [[8, 21], [9, 15]], [[10, 10], [10, 18]], [[10, 23], [10, 25]], [[11, 21], [12, 20]]] },
  "xmas-kongensnytorv": { ranges: [[[11, 14], [12, 23]]] },
  "xmas-radhuspladsen": { ranges: [[[11, 14], [12, 23]]] },
  "xmas-gammeltorv": { ranges: [[[11, 14], [12, 23]]] },
  "xmas-hojbro": { ranges: [[[11, 14], [12, 20]]] },
  hviids: { ranges: [[[11, 14], [12, 30]]] },
  dangleterre: { ranges: [[[11, 13], [12, 31]]] },
};
const fmtMD = ([m, d]) => `${d} ${["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][m - 1]}`;
// "open 19 Mar–20 Sep, 2–31 Oct & 13 Nov–3 Jan"
export const seasonLabel = (s) => {
  const o = OPEN_DATES[s.id];
  if (!o) return null;
  const parts = o.ranges.map(([a, b]) => (a[0] === b[0] ? `${a[1]}–${fmtMD(b)}` : `${fmtMD(a)}–${fmtMD(b)}`));
  return "open " + (parts.length > 1 ? parts.slice(0, -1).join(", ") + " & " + parts[parts.length - 1] : parts[0]) + (o.weekendOnly ? " (weekends only in the shoulder weeks)" : "");
};
// Is the spot in season on this exact date? Falls back to the month list.
export const isOpenOnDate = (s, date) => {
  if (!s || !(date instanceof Date)) return true;
  const o = OPEN_DATES[s.id];
  if (!o) return isAvailable(s, date.getMonth() + 1);
  const md = MD(date.getMonth() + 1, date.getDate());
  if (!o.ranges.some((r) => inRange(md, r))) return false;
  if (o.weekendOnly && o.weekendOnly.some((r) => inRange(md, r))) return date.getDay() === 0 || date.getDay() === 6;
  return true;
};
// The next date on/after `fromDate` when this seasonal spot opens again — used
// for the "all open from X" note on out-of-season days. Returns null if the
// spot has no dated season (always open) or is already open on `fromDate`.
export const nextOpeningDate = (s, fromDate) => {
  const o = s && OPEN_DATES[s.id];
  if (!o || !(fromDate instanceof Date) || isOpenOnDate(s, fromDate)) return null;
  const y = fromDate.getFullYear();
  let best = null;
  for (const yr of [y, y + 1]) {
    for (const [a] of o.ranges) {
      const dt = new Date(yr, a[0] - 1, a[1]);
      if (dt >= fromDate && (!best || dt < best)) best = dt;
    }
  }
  return best;
};
export const isClosedOnDate = (s, date) => {
  if (!s || !(date instanceof Date)) return false;
  if (!isOpenOnDate(s, date)) return true;
  const c = CLOSED_ON[s.id];
  if (!c) return false;
  if (c.months && !c.months.includes(date.getMonth() + 1)) return false;
  if (c.exceptDates && c.exceptDates.some((r) => inRange(MD(date.getMonth() + 1, date.getDate()), r))) return false;
  return c.days.includes(date.getDay());
};
// "Closed on Mondays" / "Closed Mondays & Tuesdays"
export const closedDaysLabel = (s) => {
  const c = CLOSED_ON[s.id];
  if (!c) return null;
  return `Closed ${c.days.map((d) => WEEKDAYS[d] + "s").join(" & ")}`;
};
// Why a spot doesn't work on a date: out of season (with its real season) or a weekly closing day.
export const closureReason = (s, date) => {
  if (!isOpenOnDate(s, date)) {
    const season = seasonLabel(s);
    return season ? `out of season that day — ${season}` : `out of season in ${["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"][date.getMonth()]}`;
  }
  const c = closedDaysLabel(s);
  return c ? c.charAt(0).toLowerCase() + c.slice(1) : "closed that day";
};

// ---------------------------------------------------------------------------
// Trip context — set by the planner when the visitor gives exact dates, an
// arrival / departure time or a hotel. Read by the day builders so every
// day card carries its date, its own start/end and the hotel legs.
//   planStart: Date of plan day 1 · planDays: how many plan days
//   firstFrom: hours (14 = 14:00) the visitor is free from on day 1
//   lastBy: hours the last day must end by · hotel: { name, coords, cluster }
// ---------------------------------------------------------------------------
export const TRIP = { planStart: null, planDays: 0, firstFrom: null, lastBy: null, hotel: null };
export function setTripContext(next) {
  Object.assign(TRIP, { planStart: null, planDays: 0, firstFrom: null, lastBy: null, hotel: null }, next || {});
  if (TRIP.hotel && !TRIP.hotel.cluster) {
    let cluster = "indre-by";
    let best = Infinity;
    SPOTS.forEach((s) => {
      const d = (s.coords[0] - TRIP.hotel.coords[0]) ** 2 + (s.coords[1] - TRIP.hotel.coords[1]) ** 2;
      if (d < best) {
        best = d;
        cluster = s.cluster;
      }
    });
    TRIP.hotel = { ...TRIP.hotel, id: "hotel", cluster };
  }
}
export const dayDateFor = (dayNum) => {
  if (!TRIP.planStart) return null;
  const d = new Date(TRIP.planStart);
  d.setDate(d.getDate() + dayNum - 1);
  return d;
};

// ---------------------------------------------------------------------------
// Public / bank holidays — Denmark & Sweden. Easter is movable, so each year's
// set is computed (Anonymous Gregorian computus) and cached. Used only to flag
// a chosen date as an official bank holiday on the day card (informational:
// reduced hours, many shops shut). Swedish holidays matter for the Malmö day
// trip across the Øresund; Danish holidays for everything else in Copenhagen.
// ---------------------------------------------------------------------------
const easterSunday = (year) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
};
const addDays = (date, n) => {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
};
const holKey = (date) => `${date.getMonth() + 1}-${date.getDate()}`;
// the Saturday that falls within an inclusive date range (Swedish Midsummer &
// All Saints' Day are always "the Saturday between X and Y")
const saturdayBetween = (year, m1, d1, m2, d2) => {
  for (let dt = new Date(year, m1 - 1, d1); dt <= new Date(year, m2 - 1, d2); dt.setDate(dt.getDate() + 1))
    if (dt.getDay() === 6) return new Date(dt);
  return null;
};
const _dkHolCache = {};
const _seHolCache = {};
const dkHolidays = (year) => {
  if (_dkHolCache[year]) return _dkHolCache[year];
  const E = easterSunday(year);
  const m = {};
  const set = (date, name) => { if (date) m[holKey(date)] = name; };
  set(new Date(year, 0, 1), "Nytårsdag");
  set(addDays(E, -3), "Skærtorsdag");
  set(addDays(E, -2), "Langfredag");
  set(E, "Påskedag");
  set(addDays(E, 1), "2. påskedag");
  set(addDays(E, 39), "Kristi himmelfartsdag");
  set(addDays(E, 49), "Pinsedag");
  set(addDays(E, 50), "2. pinsedag");
  set(new Date(year, 5, 5), "Grundlovsdag");
  set(new Date(year, 11, 25), "Juledag");
  set(new Date(year, 11, 26), "2. juledag");
  _dkHolCache[year] = m;
  return m;
};
const seHolidays = (year) => {
  if (_seHolCache[year]) return _seHolCache[year];
  const E = easterSunday(year);
  const m = {};
  const set = (date, name) => { if (date) m[holKey(date)] = name; };
  set(new Date(year, 0, 1), "Nyårsdagen");
  set(new Date(year, 0, 6), "Trettondedag jul");
  set(addDays(E, -2), "Långfredagen");
  set(E, "Påskdagen");
  set(addDays(E, 1), "Annandag påsk");
  set(new Date(year, 4, 1), "Första maj");
  set(addDays(E, 39), "Kristi himmelsfärdsdag");
  set(addDays(E, 49), "Pingstdagen");
  set(new Date(year, 5, 6), "Sveriges nationaldag");
  set(saturdayBetween(year, 6, 20, 6, 26), "Midsommardagen");
  set(saturdayBetween(year, 10, 31, 11, 6), "Alla helgons dag");
  set(new Date(year, 11, 25), "Juldagen");
  set(new Date(year, 11, 26), "Annandag jul");
  _seHolCache[year] = m;
  return m;
};
export const dkHolidayOn = (date) => (date instanceof Date ? dkHolidays(date.getFullYear())[holKey(date)] || null : null);
export const seHolidayOn = (date) => (date instanceof Date ? seHolidays(date.getFullYear())[holKey(date)] || null : null);


// Closing hour (24h) for a spot in a given month, if it has one.
const closingHour = (s, month) => {
  if (!s) return null;
  if (s.closesByMonth && month != null && month !== "any") return s.closesByMonth[month] ?? null;
  return CLOSE_HOUR[s.id] ?? null;
};

// A day's rough clock for the day card: 9:30 start, walked through the stops
// with real durations and travel legs — plus any stop you'd reach too close
// to its closing time (a museum that shuts at 17:00 can't be the 16:45 plan).
export const fmtClock = (h) => {
  const hh = Math.floor(h);
  const mm = Math.round(((h - hh) * 60) / 5) * 5;
  return `${mm === 60 ? hh + 1 : hh}:${String(mm === 60 ? 0 : mm).padStart(2, "0")}`;
};
const DAY_START = 10; // 10:00 — room for breakfast first; most sights open at 10 anyway
const LUNCH_H = 1; // a 1h sit-down lunch break
const LUNCH_QUICK_H = 1 / 3; // ~20 min — a slice or bakery goods on the go (the itinerary's Day 2)
// Don't break for lunch right BEFORE these stops: they sit mid-way through a
// continuous walk (the royal quarter — Marble Church & Amalienborg), so a
// lunch slotted ahead of them sends you backwards for a nearby café (Gasoline
// Grill) and then forward again. The break is deferred to the next gap after
// them — a later lunch, nearer the harbour, that doesn't backtrack.
const LUNCH_NOT_BEFORE = new Set(["amalienborg", "marmorkirken"]);
// Street-food markets you plan your whole day around (Reffen) ARE lunch, even
// when the natural route lands them in the early afternoon — so a day that
// includes one never also gets a separate lunch venue suggested.
const LUNCH_ANCHOR = new Set(["reffen"]);
// Venues within a short REAL walk of a stop — road distance via the same
// water-aware routing the legs use (a bridge or the Refshaleøen neck when
// needed), so Reffen never shows up "next to" Kastellet across the harbour.
const venueCluster = (v) => {
  if (!v._cluster) {
    let best = Infinity;
    SPOTS.forEach((sp) => {
      if (sp.cluster === "daytrip") return;
      const d = havKm(sp.coords, v.coords);
      if (d < best) {
        best = d;
        v._cluster = sp.cluster;
      }
    });
  }
  return v._cluster;
};
const venuesNear = (list, from, { month, have = [], maxMin = 12, n = 2 } = {}) =>
  nearestVenues(list, from.coords, { month, have, maxKm: 2.5, n: 30 })
    .map((v) => {
      const km = roadRoute(from, { id: v.spot || "venue", coords: v.coords, cluster: venueCluster(v) }).km;
      return { ...v, min: Math.round((km / 4.8) * 60) };
    })
    .filter((v) => v.min <= maxMin)
    .sort((a, b) => a.min - b.min)
    .slice(0, n); // a 45-min lunch break
// The day's clock, walked stop by stop with real durations and travel legs.
// Also: the hotel legs out and back (when a hotel is set), a lunch break
// where ~12:30 falls (with venues near that point — unless the day already
// eats then, e.g. Reffen or a smørrebrød lunch), a dinner idea near the last
// stop, any stop reached too close to its closing time, and — on the last
// day — whether the day runs past the visitor's leave-by time.
// `lunchAfter` (index of the stop lunch follows) is the visitor's own choice
// when they've moved the lunch break — it overrides the ~12:00 rule.
const DINNER_H = 1.5; // a sit-down dinner the visitor added to the day
// The Extreme Day Trip (/edt) is a hand-timed, one-off plan. It deliberately
// breaks the planner's normal rules (per-spot durations, the ~12:00 lunch
// insertion, closing-time checks) and instead shows the exact clock the /edt
// page lays out. Keyed by spot id → arrival time.
export const EDT_TIMES = {
  amagergade: "09:45",
  christiania: "10:15",
  broens: "11:30",
  nyhavn: "12:15",
  canaltour: "12:45",
  amalienborg: "14:00",
  marmorkirken: "14:15",
  torvehallerne: "14:30",
  stroget: "14:45",
  buka: "16:00",
  tivoli: "16:30",
};
// Where a newly-added stop slots into an EDT day: keep the day ordered by the
// canonical EDT timetable (so e.g. Torvehallerne lands in Rundetårn's slot),
// never re-clustered by geography like a normal day.
export const edtInsertIndex = (items, spot) => {
  const order = Object.keys(EDT_TIMES);
  const rank = (id) => { const i = order.indexOf(id); return i < 0 ? 999 : i; };
  const idx = items.findIndex((s) => rank(s.id) > rank(spot.id));
  return idx < 0 ? items.length : idx;
};
// EDT-only duration overrides — a stop can read differently on the packed day
// than in the normal planner (e.g. a quick 30-min Nyhavn pass vs a leisurely
// hour). { dur (hours), label }. Applied for display and the removal dwell.
export const EDT_DUR = {
  nyhavn: { dur: 0.5, label: "~30 min" },
  christiania: { dur: 1, label: "~1h" },
};
export const dayTimeline = (items, month, dayNum = 0, { lunchAfter = null, dinnerPick = null, edt = false, edtOffset = 0 } = {}) => {
  if (!items.length) return null;
  // Extreme Day Trip exception — based on the /edt page's timetable, no auto
  // lunch/dinner, no closing-time warnings. Times recompute when stops are
  // dropped: every later stop is pulled earlier by the dwell (`dur`) of each
  // removed stop, unless another kept stop still fills that slot (e.g. swapping
  // Torvehallerne in for Rundetårn at the same time). `edtOffset` (minutes)
  // shifts the whole day for a later/earlier arrival — default 0 = ready 09:00.
  if (edt) {
    const EDT_ORDER = Object.keys(EDT_TIMES);
    const EDT_ALT = new Set(["torvehallerne"]); // optional swaps that share a slot, not baseline stops
    const off = Number.isFinite(edtOffset) ? edtOffset : 0;
    const toMin = (t) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; };
    const fmt = (x) => `${String(Math.floor(x / 60)).padStart(2, "0")}:${String(x % 60).padStart(2, "0")}`;
    const present = new Set(items.map((s) => s.id));
    const presentTimes = new Set(items.map((s) => EDT_TIMES[s.id]).filter(Boolean));
    let clock = null;
    let prevIdx = null;
    const at = items.map((s) => {
      const canon = EDT_TIMES[s.id];
      if (canon == null) return "";
      const idx = EDT_ORDER.indexOf(s.id);
      if (clock === null) {
        // first present stop: pull earlier by the dwell of any dropped stops
        // that sit before it in the timetable, so skipping the opener (e.g.
        // Amagergade) shifts Christiania and the whole rest of the day earlier
        // rather than just starting late.
        let lead = 0;
        for (let k = 0; k < idx; k++) {
          const skid = EDT_ORDER[k];
          if (!present.has(skid) && !EDT_ALT.has(skid) && !presentTimes.has(EDT_TIMES[skid])) {
            const dd = EDT_DUR[skid] ? EDT_DUR[skid].dur : (spotById(skid)?.dur || 0);
            lead += Math.round(dd * 60);
          }
        }
        clock = toMin(canon) - lead;
      } else {
        let delta = toMin(EDT_TIMES[EDT_ORDER[idx]]) - toMin(EDT_TIMES[EDT_ORDER[prevIdx]]);
        const lo = Math.min(prevIdx, idx), hi = Math.max(prevIdx, idx);
        for (let k = lo + 1; k < hi; k++) {
          const skid = EDT_ORDER[k];
          if (!present.has(skid) && !EDT_ALT.has(skid) && !presentTimes.has(EDT_TIMES[skid])) {
            const dd = EDT_DUR[skid] ? EDT_DUR[skid].dur : (spotById(skid)?.dur || 0);
            delta -= Math.round(dd * 60);
          }
        }
        clock += Math.max(5, delta);
      }
      prevIdx = idx;
      return fmt(clock + off);
    });
    // End of day = the last stop's arrival + its stay, so dropping or adding
    // stops moves the finish time too (Tivoli caps at a 3h evening). Falls back
    // to the clock + offset only if the day somehow has no timed stops.
    let endMin = 22 * 60 + off;
    let lastI = -1;
    for (let i = at.length - 1; i >= 0; i--) { if (at[i]) { lastI = i; break; } }
    if (lastI >= 0) {
      const last = items[lastI];
      const lastDur = last.id === "tivoli" ? 3 : (EDT_DUR[last.id] ? EDT_DUR[last.id].dur : (spotById(last.id)?.dur || 0));
      endMin = toMin(at[lastI]) + Math.round(lastDur * 60);
    }
    return {
      start: at.find(Boolean) || "09:15",
      end: `~${fmt(endMin)}`,
      endH: Math.floor(endMin / 60),
      tight: [],
      at,
      lunch: null,
      lunchCovered: null,
      dinner: null,
      hotelOut: null,
      hotelBack: null,
      backAt: null,
      over: null,
    };
  }
  const pinnedLunch = Number.isInteger(lunchAfter) ? Math.max(0, Math.min(items.length - 1, lunchAfter)) : null;
  const isFirst = dayNum === 1 && TRIP.firstFrom != null;
  const isLast = dayNum > 0 && dayNum === TRIP.planDays && TRIP.lastBy != null;
  const allDaytrip = items.every((s) => isDaytripSpot(s));
  const hotel = TRIP.hotel && !allDaytrip ? TRIP.hotel : null;
  const hotelOut = hotel ? travelBetween(hotel, items[0]) : null;
  const hotelBack = hotel ? travelBetween(items[items.length - 1], hotel) : null;
  const ids = items.map((s) => s.id);
  // the curated Day 2 (Torvehallerne → Latin Quarter → Strøget → Tivoli) eats
  // on the go, like the itinerary page says: Bear Slice or bakery goods
  const quickLunch = ITINERARY_3D[1].filter((id) => ids.includes(id)).length >= 4;
  const lunchH = quickLunch ? LUNCH_QUICK_H : LUNCH_H;

  const walk = (t0, withLunch) => {
    let t = t0;
    const tight = [];
    const at = [];
    let lunch = null;
    if (hotelOut) t += hotelOut.min / 60;
    items.forEach((s, i) => {
      if (i > 0) {
        const leg = travelBetween(items[i - 1], s);
        if (leg) t += leg.min / 60;
      }
      // lunch slots in between stops once the clock passes ~12:00 — or right
      // where the visitor put it. When auto-placing (not pinned), never break
      // right before a mid-walk royal stop — defer to the next gap after it.
      if (
        withLunch &&
        !lunch &&
        i > 0 &&
        (pinnedLunch != null ? pinnedLunch === i - 1 : t >= 12 && !LUNCH_NOT_BEFORE.has(s.id))
      ) {
        lunch = { afterIdx: i - 1, at: fmtClock(t), coords: items[i - 1].coords };
        t += lunchH;
      }
      at.push(fmtClock(t));
      const close = closingHour(s, month);
      if (close != null && t + s.dur > close)
        tight.push({ id: s.id, name: s.name, close, arrival: fmtClock(t) });
      t += s.dur;
    });
    // a short day that ends before lunch got started: eat after the last stop
    if (withLunch && !lunch && (pinnedLunch != null || (t >= 12 && t <= 14.5))) {
      lunch = { afterIdx: items.length - 1, at: fmtClock(t), coords: items[items.length - 1].coords };
      t += lunchH;
    }
    // a pure day-trip day includes the ride out and back
    if (allDaytrip) {
      const out = dayTripTransit(items[0]);
      const back = dayTripTransit(items[items.length - 1]);
      if (out) t += out.min / 60;
      if (back) t += back.min / 60;
    }
    const endAt = t;
    if (hotelBack) t += hotelBack.min / 60;
    return { t, endAt, tight, at, lunch };
  };

  let start = isFirst ? Math.max(DAY_START, TRIP.firstFrom) : DAY_START;
  // first pass without lunch: does the day already eat at lunchtime?
  const dry = walk(start, false);
  const covered =
    items.find((s) => LUNCH_ANCHOR.has(s.id)) ||
    items.find((s, i) => {
      if (!LUNCH_COVER.has(s.id)) return false;
      const [h, m] = dry.at[i].split(":").map(Number);
      const a = h + m / 60;
      // a food/long stop whose visit spans the midday window IS the lunch — this
      // catches both a short stop around lunchtime and a long one like Tivoli
      // (arrives ~10:00 but you're inside over lunch, and you eat there)
      return a <= 14 && a + s.dur >= 12;
    });
  // no lunch break on day-trip days (the trip guides cover lunch), when the
  // day only starts after lunch, or when a lunch stop is already in the plan
  const withLunch = pinnedLunch != null || (!allDaytrip && start < 13 && !covered);
  let res = walk(start, withLunch);
  // A Tivoli evening on a short day would otherwise put the gardens at noon —
  // slide the morning later so arrival lands ~15:00 (at most a 2h-lie-in)
  if (!isFirst && items.length > 1 && items[items.length - 1].id === "tivoli") {
    const arrival = res.endAt - items[items.length - 1].dur;
    if (arrival < 15) {
      start = Math.min(DAY_START + (15 - arrival), 11.5);
      res = walk(start, withLunch);
    }
  }
  let { t, endAt, tight, at, lunch } = res;
  if (lunch) {
    lunch.quick = quickLunch;
    lunch.min = Math.round(lunchH * 60);
    const pool = quickLunch ? LUNCH_VENUES.filter((v) => v.quick) : LUNCH_VENUES.filter((v) => !v.quick);
    lunch.venues = venuesNear(pool, items[lunch.afterIdx], { month, have: ids, maxMin: quickLunch ? 15 : 12 });
    if (!lunch.venues.length && quickLunch) lunch.venues = venuesNear(LUNCH_VENUES, items[lunch.afterIdx], { month, have: ids });
  }
  const lunchCovered = covered && pinnedLunch == null ? { name: covered.name, id: covered.id } : null;
  if (lunch) lunch.pinned = pinnedLunch != null;

  // dinner: an evening that ends in town without a food stop gets two ideas
  // near the last stop (near the hotel on a day-trip day)
  let dinner = null;
  const last = items[items.length - 1];
  const dinnerCovered = items.some((s, i) => {
    if (!DINNER_COVER.has(s.id)) return false;
    const [h, m] = at[i].split(":").map(Number);
    return h + m / 60 >= 16.5 || i === items.length - 1;
  });
  if (dinnerPick) {
    // the visitor added a dinner: it sits after the last stop (never before
    // 18:00) and takes ~1½h — the day and the hotel leg end after it
    const dinnerAt = Math.max(endAt, 18);
    dinner = { at: fmtClock(dinnerAt), picked: dinnerPick, venues: [] };
    const shift = dinnerAt + DINNER_H - endAt;
    endAt += shift;
    t += shift;
  } else if (!dinnerCovered && endAt >= 15 && endAt <= 21 && !isLast) {
    const from = allDaytrip ? hotel : last;
    const venues = from ? venuesNear(DINNER_VENUES, from, { month, have: ids, maxMin: allDaytrip ? 15 : 12 }) : [];
    // dinner is dinner — never before 18:00 even when the day wraps early
    if (venues.length) dinner = { at: fmtClock(Math.max(endAt, 18)), venues };
  }
  const over = isLast && endAt > TRIP.lastBy + 0.01 ? { by: fmtClock(TRIP.lastBy), end: fmtClock(endAt) } : null;
  return {
    start: fmtClock(start),
    end: "~" + fmtClock(endAt),
    endH: endAt,
    tight,
    at,
    lunch,
    lunchCovered,
    dinner,
    hotelOut,
    hotelBack,
    backAt: hotelBack ? fmtClock(t) : null,
    over,
  };
};

function orderDayItems(items, month) {
  if (items.length <= 1) return items.slice();
  const daytrips = items.filter(isDaytripSpot);
  const anchorsAll = items.filter((s) => !isDaytripSpot(s) && isAnchorSpot(s));
  // Early-closing anchors (e.g. the Zoo in winter) shouldn't be scheduled last
  // when other stops stay open later — visit them first.
  const earlyAnchors = anchorsAll.filter((s) => {
    const c = closingHour(s, month);
    return c != null && c < 18;
  });
  // The Zoo is a big half-day out west — it always leads its day (a long ride
  // out, and it closes as early as 16:00 in winter), never the finale of a walk.
  const zoo = anchorsAll.find((s) => s.id === "zoo");
  if (zoo && !earlyAnchors.includes(zoo)) earlyAnchors.push(zoo);
  const anchors = anchorsAll.filter((s) => !earlyAnchors.includes(s));
  let pool = items.filter((s) => !isDaytripSpot(s) && !isAnchorSpot(s));
  // far-out, early-closing stops (Den Blå Planet) lead the day — pulled out of
  // the walkable pool and placed first, before the centre cluster flows on
  const leaders = [];
  pool = pool.filter((s) => {
    if (FRONT_LEADERS.has(s.id)) { leaders.push(s); return false; }
    return true;
  });
  // keep the Gasoline Grill that's "inside Tivoli" glued right before Tivoli
  const tivoli = anchors.find((s) => s.id === "tivoli");
  let grill = null;
  if (tivoli) {
    const gi = pool.findIndex((s) => s.id === "gasolinegrill");
    if (gi >= 0) grill = pool.splice(gi, 1)[0];
  }
  const anchorCoord = earlyAnchors.length
    ? earlyAnchors[0].coords
    : anchors.length
    ? anchors[0].coords
    : null;
  // The covered food market is a breakfast/morning opener — when it's on the
  // day it leads the walk (a market breakfast), and the rest flows outward from
  // it. Without this the pure distance optimiser can bury it mid-route or put a
  // neighbour (e.g. Rundetårn, a 5-min walk away) ahead of it.
  const openerIdx = pool.findIndex((s) => DAY_OPENERS.has(s.id));
  const opener = openerIdx >= 0 ? pool.splice(openerIdx, 1)[0] : null;
  let ordered = optimizeOrder(pool, anchorCoord);
  if (opener) {
    // orient the rest so the walk flows out from the opener's nearest stop —
    // but never if that reversal would scramble the time-of-day flow
    if (
      ordered.length > 1 &&
      roadKmCoords(ordered[ordered.length - 1].coords, opener.coords) <
        roadKmCoords(ordered[0].coords, opener.coords)
    ) {
      const rev = ordered.slice().reverse();
      if (orderPenalty(rev) <= orderPenalty(ordered)) ordered = rev;
    }
    ordered = [opener, ...ordered];
  }
  const tail = anchors.flatMap((s) => (s.id === "tivoli" && grill ? [grill, s] : [s]));
  let day = [...daytrips, ...earlyAnchors, ...leaders, ...ordered, ...tail];

  // Lunch belongs at lunchtime: if an unpinned lunch stop (smørrebrød, a
  // standalone Gasoline Grill) ended up LAST on a long day, it would start
  // mid-afternoon — pull it forward to where ~12:30 actually falls instead.
  const LUNCH = new Set(["smorrebrod", "gasolinegrill"]);
  const li = day.findIndex((s) => LUNCH.has(s.id) && !s.pinned);
  if (li >= 3 && li === day.length - 1) {
    let clock = 10; // days start around 10:00
    let insertAt = li;
    for (let i = 0; i < day.length - 1; i++) {
      clock += day[i].dur;
      if (clock >= 12.25) {
        insertAt = i + 1;
        break;
      }
      const leg = travelBetween(day[i], day[i + 1]);
      if (leg) clock += leg.min / 60;
    }
    const lunchStart =
      10 +
      day.slice(0, li).reduce((t, s, i) => {
        const leg = i > 0 ? travelBetween(day[i - 1], s) : null;
        return t + s.dur + (leg ? leg.min / 60 : 0);
      }, 0);
    if (lunchStart >= 13 && insertAt < li) {
      const [lunch] = day.splice(li, 1);
      day.splice(insertAt, 0, lunch);
    }
  }

  // These spots must sit back to back within a day — if the optimiser let
  // something slip between them (e.g. Strøget landing between the Latin
  // Quarter and Gråbrødretorv), reunite them: the earlier-time stop stays
  // put, its partner moves in right after it. Always pairs that are a few
  // minutes apart, so the detour is negligible. (Not every COUPLED pair —
  // e.g. Sankt Peders↔Torvehallerne share a day but keep their own times.)
  // Run once here and again after the directional chains below, since a chain
  // can nudge one half of a pair away (e.g. Istedgade → Enghave Plads pulling
  // Enghave Plads off its neighbouring Bageriet BRØD).
  const enforceAdjacent = () =>
    ALWAYS_ADJACENT.forEach(([x, y]) => {
      const ix = day.findIndex((s) => s.id === x);
      const iy = day.findIndex((s) => s.id === y);
      if (ix < 0 || iy < 0 || Math.abs(ix - iy) === 1) return;
      const anchorFirst = (day[ix].order ?? 3) <= (day[iy].order ?? 3) ? x : y;
      const mi = day.findIndex((s) => s.id === (anchorFirst === x ? y : x));
      const [moved] = day.splice(mi, 1);
      day.splice(day.findIndex((s) => s.id === anchorFirst) + 1, 0, moved);
    });
  enforceAdjacent();

  // Directional chains: X must come right before Y, in this order — the
  // optimiser treats them as near-equal distance, but visitors experience
  // them as one promenade (the royal-quarter walk runs Marble Church →
  // palace → up the waterfront to the Mermaid; the Nyhavn stroll before
  // the boat that departs from it).
  // The Vesterbro promenade (Istedgade → Enghave Plads → Carlsberg brewhouse →
  // Kødbyen) only reads east-to-west when it actually starts at Istedgade. If
  // Istedgade isn't on the day, forcing Enghave Plads ahead of the Carlsberg
  // stops drags the easternmost square to the front — so a western opener (the
  // Carlsberg bakery) zig-zags east then back west. Without Istedgade, let the
  // distance optimiser place Enghave Plads and keep only the Carlsberg chain.
  const hasIstedgade = day.some((s) => s.id === "istedgade");
  const CHAINED = [
    ["marmorkirken", "amalienborg"],
    ["amalienborg", "nyboder"],
    ["nyboder", "littlemermaid"],
    ["nyhavn", "canaltour"],
    // the Vesterbro promenade: from the station down Istedgade to Enghave
    // Plads, on to the Carlsberg brewhouse, then dinner in Kødbyen
    ["istedgade", "enghaveplads"],
    ...(hasIstedgade
      ? [
          ["enghaveplads", "carlsbergbyen"],
          ["enghaveplads", "carlsberg"],
        ]
      : []),
    ["enghaveplads", "smorrebrod"], // day 4: lunch right after the square
    ["smorrebrod", "carlsbergbyen"], // ...then the district stroll
    // keep Københavns Bageri (a western Carlsberg-area bakery) glued to the
    // Carlsberg stops when they share a day, so the breakfast opener leads
    // straight into the brewery district instead of being stranded across town
    ["kobenhavnsbageri", "carlsberg"],
    ["kobenhavnsbageri", "carlsbergbyen"],
    ["carlsbergbyen", "carlsberg"],
    ["carlsberg", "koedbyen"],
    ["helsingor", "louisiana"], // Kronborg first (closes early), Louisiana on the way back (open late)
  ];
  CHAINED.forEach(([x, y]) => {
    const ix = day.findIndex((s) => s.id === x);
    const iy = day.findIndex((s) => s.id === y);
    if (ix < 0 || iy < 0 || ix === iy - 1) return;
    const [moved] = day.splice(iy, 1);
    day.splice(day.findIndex((s) => s.id === x) + 1, 0, moved);
  });
  // Re-unite any always-adjacent pair a chain may have split apart.
  enforceAdjacent();

  // Neighbourhood flows: a district done as one continuous walk in a local's
  // order — an Østerbro day starts from the harbour side and works north:
  // Broens (by the Nyhavn bridge, on the way up) comes first, then Bosses
  // (bakery breakfast), the rows, and the day ends up in Nordhavn. Never a
  // 20-min metro backtrack to Broens after Nordhavn. Only the relative order
  // of that district's stops is fixed; the rest of the day keeps the
  // optimiser's arrangement. A flow only applies when the day actually
  // contains that district's spots — so a classic harbour Day 1 (Nyhavn →
  // canal boat → Broens last) is never flipped by the Østerbro flow.
  const CLUSTER_FLOW = {
    osterbro: ["broens", "bosses", "brumleby", "ismageriet", "juno", "swim-sandkaj", "nordhavn"],
  };
  // A day shouldn't close down at 17:00: if the final daytime stop shuts by
  // 19:00 but the day holds something that stays open later (or never closes —
  // parks, Nyhavn, the harbourfront), that becomes the closer. An
  // always-adjacent pair moves as a block so it never gets split. (GoBoat
  // keeps the very last slot when it's in the day — rule below.)
  const closesLate = (s) => {
    if (s.id === "goboat") return true; // sails till sunset
    const c = closingHour(s, month);
    return c == null || c > 19;
  };
  if (day.length > 1) {
    let lastNA = day.length - 1;
    while (lastNA >= 0 && isAnchorSpot(day[lastNA])) lastNA--;
    if (lastNA >= 0 && !closesLate(day[lastNA])) {
      let li = -1;
      for (let i = lastNA - 1; i >= 0; i--) {
        if (!isAnchorSpot(day[i]) && closesLate(day[i])) {
          li = i;
          break;
        }
      }
      if (li >= 0) {
        const ids = new Set([day[li].id]);
        ALWAYS_ADJACENT.forEach(([x, y]) => {
          if (day[li].id === x && day[li + 1]?.id === y) ids.add(y);
          if (day[li].id === y && day[li - 1]?.id === x) ids.add(x);
        });
        const moving = day.filter((s) => ids.has(s.id));
        day = day.filter((s) => !ids.has(s.id));
        let end = day.length;
        while (end > 0 && isAnchorSpot(day[end - 1])) end--;
        day.splice(end, 0, ...moving);
      }
    }
  }
  // GoBoat sails till sunset — on a day that has it, it's the last thing on
  // the schedule, full stop (the optimiser's geography otherwise sneaks a
  // harbour bath in after it). Evening anchors like Tivoli still come after.
  const gi = day.findIndex((s) => s.id === "goboat");
  if (gi >= 0) {
    let lastNonAnchor = day.length - 1;
    while (lastNonAnchor >= 0 && isAnchorSpot(day[lastNonAnchor])) lastNonAnchor--;
    if (gi < lastNonAnchor) {
      const [g] = day.splice(gi, 1);
      day.splice(lastNonAnchor, 0, g);
    }
  }
  return day;
}

// Label a day by its dominant neighbourhood zone.
function dayLabel(items) {
  if (!items.length) return null;
  const counts = {};
  items.forEach((s) => {
    const z = ZONE_OF[s.cluster] || s.cluster;
    counts[z] = (counts[z] || 0) + 1;
  });
  // Show every area the day actually spans, most-visited first (ties by zone order).
  const labels = Object.keys(counts)
    .sort((x, y) => counts[y] - counts[x] || zoneRank(x) - zoneRank(y))
    .map((z) => ZONE_LABEL[z])
    .filter(Boolean);
  if (!labels.length) return null;
  return labels.join(" · ");
}

// The smørrebrød lunch has classic venues around town, grouped by area. The
// day's centroid picks the AREA; within that area the venue rotates daily
// (day-of-year seed) so repeat planners don't always get sent to the same
// table — and lunch never sends you across the city. The venue only changes
// the stop's name/coords — never its position in the day (that's the
// visitor's to arrange).
const SMORREBROD_LOCATIONS = [
  { name: "Restaurant Schønnemann", coords: [55.68277, 12.57608], area: "city" },
  { name: "Aamanns 1921", coords: [55.68021, 12.57591], area: "city" },
  { name: "Restaurant Kronborg", coords: [55.67758, 12.57442], area: "city" },
  // The Danish-classics houses from /danish-food, folded in for a more
  // diverse lunch shuffle (user request — Axelborg Bodega & Frk. Barners
  // Kælder back in after their earlier drop)
  { name: "Københavner Caféen", coords: [55.6782, 12.5762], area: "city" }, // Badstuestræde 10
  { name: "Restaurant Karla", coords: [55.6745, 12.5712], area: "city" }, // Dantes Plads 1
  { name: "Restaurant Puk", coords: [55.6766, 12.5748], area: "city" }, // Vandkunsten 8
  { name: "Det Lille Apotek", coords: [55.6801, 12.5751], area: "city" }, // Store Kannikestræde 15
  { name: "Axelborg Bodega", coords: [55.6755, 12.5645], area: "city" }, // Axeltorv 1
  { name: "Told & Snaps", coords: [55.67995, 12.59173], area: "nyhavn" },
  { name: "Nyhavns Færgekro", coords: [55.6799, 12.5906], area: "nyhavn" },
  { name: "Nyhavnskroen", coords: [55.6802, 12.5889], area: "nyhavn" }, // Nyhavn 47
  { name: "Christianshavns Færgecafé", coords: [55.6755, 12.59248], area: "christianshavn" },
  // The west side (day 4): Klubben by Enghave Plads, Frk. Barners on
  // Helgolandsgade + three in Carlsberg Byen
  { name: "Restaurant Klubben", coords: [55.67112, 12.54577], area: "vesterbro" }, // Enghavevej 4
  { name: "Frk. Barners Kælder", coords: [55.6707, 12.559], area: "vesterbro" }, // Helgolandsgade 8A
  { name: "Wulff & Konstali", coords: [55.6672, 12.53512], area: "vesterbro" }, // Constantin Hansens Gade 3
  { name: "Dolores", coords: [55.66533, 12.53708], area: "vesterbro" }, // Kildepladsen
  { name: "Aamanns Genbo", coords: [55.66563, 12.53716], area: "vesterbro" }, // Kildepladsen 8
  // Frederiksberg (user-pinned)
  { name: "Krøgers Familiehave", coords: [55.67124, 12.53106], area: "frederiksberg" }, // Pile Allé 18
  { name: "Restaurant Allegade 10", coords: [55.676, 12.53322], area: "frederiksberg" }, // Allegade 10
  { name: "Café Lindevang", coords: [55.67923, 12.51272], area: "frederiksberg" }, // Sløjfen 6
];

// Stable-per-day rotation index into a venue pool
const dayOfYear = () =>
  Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0).getTime()) / 864e5);

function resolveSmorrebrod(items) {
  const idx = items.findIndex((s) => s.id === "smorrebrod" && !s.pinned);
  if (idx < 0) return items;
  const others = items.filter((_, i) => i !== idx).map((s) => s.coords).filter(Boolean);
  if (!others.length) return items; // alone in a day — keep the default list of venues
  const centroid = [
    others.reduce((a, c) => a + c[0], 0) / others.length,
    others.reduce((a, c) => a + c[1], 0) / others.length,
  ];
  let best = SMORREBROD_LOCATIONS[0];
  let bestD = Infinity;
  SMORREBROD_LOCATIONS.forEach((cand) => {
    const d = havKm(cand.coords, centroid);
    if (d < bestD) {
      bestD = d;
      best = cand;
    }
  });
  // rotate among the winning area's venues — same venue all day, a different
  // one tomorrow; single-venue areas just use the one
  const pool = SMORREBROD_LOCATIONS.filter((v) => v.area === best.area);
  const pick = pool.length > 1 ? pool[dayOfYear() % pool.length] : best;
  const copy = items.slice();
  copy[idx] = { ...copy[idx], coords: pick.coords, note: `at ${pick.name}` };
  return copy;
}

// Multi-branch bakery brands — like the smørrebrød lunch, the day's centroid
// picks the nearest branch so a bakery stop never sends you across town.
// Branch lists mirror the addresses on /bakeries; coords are approximate.
const BAKERY_BRANCHES = {
  hart: [
    { street: "Gammel Mønt 4", coords: [55.6830, 12.5783] },
    { street: "Store Kongensgade 75", coords: [55.6838, 12.5876] },
    { street: "Refshalevej 159A", coords: [55.6908, 12.6135] },
    { street: "Victor Borges Plads 2", coords: [55.6979, 12.5765] },
    { street: "Julius Bloms Gade 32", coords: [55.6877, 12.5586] },
  ],
  buka: [
    { street: "Store Kongensgade 18", coords: [55.6810, 12.5870] },
    { street: "Frederiksberggade 21", coords: [55.6776, 12.5694] },
    { street: "Esplanaden 14", coords: [55.6878, 12.5945] },
    { street: "Falkoner Allé 26B", coords: [55.6806, 12.5389] },
  ],
  andersenmaillard: [
    { street: "Ny Østergade 15", coords: [55.6803, 12.5835] },
    { street: "Nørrebrogade 62", coords: [55.6901, 12.5564] },
    { street: "Antwerpengade 10", coords: [55.7094, 12.5975] },
  ],
  detrenebrod: [
    { street: "Rosenvængets Allé 17", coords: [55.7040, 12.5767] },
    { street: "Aksel Møllers Have 2", coords: [55.6783, 12.5277] },
  ],
};

function resolveBakeryBranches(items) {
  const ids = items.filter((s) => BAKERY_BRANCHES[s.id] && !s.pinned);
  if (!ids.length) return items;
  // the curated crawl day keeps its hand-picked branches — distance-picking is
  // for days the visitor builds themselves
  if (BAKERY_WALK.every((id) => items.some((s) => s.id === id))) return items;
  const pts = items.map((s) => s.coords).filter(Boolean);
  if (!pts.length) return items;
  const centroid = [
    pts.reduce((a, c) => a + c[0], 0) / pts.length,
    pts.reduce((a, c) => a + c[1], 0) / pts.length,
  ];
  const copy = items.slice();
  ids.forEach((s) => {
    let best = null;
    let bestD = Infinity;
    BAKERY_BRANCHES[s.id].forEach((b) => {
      const d = havKm(b.coords, centroid);
      if (d < bestD) {
        bestD = d;
        best = b;
      }
    });
    const idx = items.indexOf(s);
    // derive from the canonical name (never the already-suffixed one) so a
    // rebuild doesn't append the branch street again and again
    const base = SPOTS.find((x) => x.id === s.id)?.name ?? s.name;
    copy[idx] = { ...s, name: `${base} (${best.street})`, coords: best.coords };
  });
  return copy;
}

// Curated-itinerary pressure valves: when a locked itinerary day runs long,
// these are the stops to drop or shorten first — surfaced on the day card so
// it's easy to see what to skip. (Day 1: Nyboder, day 2: the Round Tower
// climb, day 3: the Our Saviour's spire climb.)
export const SKIP_HINTS = {
  nyboder:
    "Short on time or want to see the change of guards? Skip Nyboder and be at Amalienborg right before 12.00.",
  rundetaarn:
    "Short on time or needing a less busy day? Then skip the Round Tower climb — admire it from the street and win back about an hour",
  oursaviour:
    "Short on time or needing a less busy day? Then skip the spire climb — the church itself and the canal views below are free",
  louisiana:
    "Short on time or needing a less busy day? Then skip Louisiana, Kronborg Castle and the Helsingør old town already make a nice coast day",
  bispebjerg:
    "Not up for two cemeteries? Then skip Bispebjerg — Assistens is the one to keep — unless it's late April, when its cherry-blossom avenue is in full Sakura bloom",
};

// If this day is (still) one of the curated itinerary days, which stop is the
// pressure valve? Subset match, so a day the visitor added a stop to keeps its
// identity; a day that lost curated stops loses the hint with them.
export const curatedSkipHint = (items) => {
  // every stop on the day that carries a skip hint (a day can offer more than
  // one). Shown on any day the stop appears, so the themed itineraries get the
  // hints too.
  const hits = items.filter((s) => SKIP_HINTS[s.id]).map((s) => ({ id: s.id, text: SKIP_HINTS[s.id] }));
  // The Glyptotek ↔ Home of Carlsberg "pick one" hint only makes sense when
  // BOTH sit on the same day (e.g. the returning-visitor itinerary). On a day
  // with just one of them — like a winter day pairing the Glyptotek with
  // Københavns Museum — it would point at a stop that isn't there, so it's
  // added here only when the pair is present, never via the flat map above.
  const ids = new Set(items.map((s) => s.id));
  if (ids.has("glyptoteket") && ids.has("carlsberg")) {
    hits.push({ id: "glyptoteket", text: "Short on time or needing a less busy day? Then skip the Glyptotek — or the Home of Carlsberg if you prefer" });
    hits.push({ id: "carlsberg", text: "Short on time or needing a less busy day? Then skip the Home of Carlsberg — or the Glyptotek if you prefer" });
  }
  return hits.length ? hits : null;
};

// Build a finished day object from a set of spots (also used when a stop is
// removed after the plan is made, so we don't rebuild the whole trip).
// Date-aware extras for a day card: its calendar date (when the trip has
// dates) and which of its stops are closed on that weekday.
const dateFields = (items, dayNum) => {
  const date = dayDateFor(dayNum);
  const hasMalmo = items.some((s) => s.id === "malmo");
  return {
    date,
    closedIds: date ? items.filter((s) => isClosedOnDate(s, date)).map((s) => s.id) : [],
    dkHoliday: date ? dkHolidayOn(date) : null,
    seHoliday: date && hasMalmo ? seHolidayOn(date) : null,
  };
};

export function buildDayFromItems(items, dayNum, month, { curated = false } = {}) {
  const ordered = orderDayItems(resolveSmorrebrod(resolveBakeryBranches(items)), month);
  const hours = dayHoursWithTravel(ordered);
  return {
    day: dayNum,
    uid: ordered.length ? `d${dayNum}-${ordered[0].id}` : `empty-${dayNum}`,
    items: ordered,
    hours,
    walk: walkTotals(ordered),
    timeline: dayTimeline(ordered, month, dayNum),
    area: dayLabel(ordered),
    packed: hours > 7,
    // Skip hints are a curated-itinerary pressure valve — only meaningful on a
    // premade itinerary day, never on a self-made selection.
    curated,
    skip: curated ? curatedSkipHint(ordered) : null,
    ...dateFields(ordered, dayNum),
  };
}

// Recompute a day AFTER the user manually rearranges (or removes) stops.
// Unlike buildDayFromItems, this keeps the exact item order the user set —
// only the smørrebrød venue (rotated daily, position untouched) is refreshed —
// and preserves the day's stable identity (uid / day number) so the card and
// its map simply reflow to the new order without remounting.
export function rebuildDayOrder(items, day, month, { reflow = false } = {}) {
  // a smørrebrød pinned inside Torvehallerne loses its pin (and the market
  // venue note) once the market itself is removed from the day
  const unpinned = items.map((s) =>
    s.pinned && s.id === "smorrebrod" && !items.some((x) => x.id === "torvehallerne")
      ? { ...SPOTS.find((x) => x.id === "smorrebrod") }
      : s
  );
  // `reflow` re-optimises the day's walking route (used when a stop is added to
  // a day) so the newcomer lands in a sensible place and any stale zig-zag is
  // corrected; the default keeps the exact order (manual drags, removals).
  let resolved = reflow
    ? orderDayItems(resolveSmorrebrod(resolveBakeryBranches(unpinned)), month)
    : resolveSmorrebrod(resolveBakeryBranches(unpinned));
  // Tivoli is always the day's evening cap — even when a manual drag left it
  // first. The Gasoline Grill "inside Tivoli" rides along, right before it.
  const ti = resolved.findIndex((s) => s.id === "tivoli");
  if (ti >= 0 && ti < resolved.length - 1) {
    const tiv = resolved[ti];
    const grill = resolved.find((s) => s.id === "gasolinegrill");
    resolved = resolved.filter((s) => s.id !== "tivoli" && s.id !== "gasolinegrill");
    resolved = grill ? [...resolved, grill, tiv] : [...resolved, tiv];
  }
  // Day 5 runs Helsingør first: Kronborg in the morning, then Louisiana on
  // the way back — the museum stays open late, the castle doesn't. Kept even
  // after a manual drag, or the train notes point the wrong way.
  const hi5 = resolved.findIndex((s) => s.id === "helsingor");
  const li5 = resolved.findIndex((s) => s.id === "louisiana");
  if (li5 >= 0 && hi5 > li5) {
    const hel = resolved[hi5];
    resolved = resolved.filter((s) => s.id !== "helsingor");
    resolved.splice(resolved.findIndex((s) => s.id === "louisiana"), 0, hel);
  }
  const hours = dayHoursWithTravel(resolved);
  // EDT-only duration overrides for display (e.g. a quick 30-min Nyhavn)
  if (day.edt) {
    resolved = resolved.map((s) => (EDT_DUR[s.id] ? { ...s, dur: EDT_DUR[s.id].dur, durLabel: EDT_DUR[s.id].label } : s));
  }
  return {
    ...day,
    items: resolved,
    hours,
    walk: walkTotals(resolved),
    timeline: dayTimeline(resolved, month, day.day, { lunchAfter: day.lunchAfter, dinnerPick: day.dinnerPick, edt: day.edt, edtOffset: day.edtOffset || 0 }),
    area: dayLabel(resolved),
    packed: day.edt ? false : hours > 7,
    // Keep the day's curated identity (set at build time) and only surface skip
    // hints on a curated/premade day — never on a self-made plan.
    skip: day.edt || !day.curated ? null : curatedSkipHint(resolved),
    ...dateFields(resolved, day.day),
  };
}

// Build a plan from an explicit day-by-day arrangement — used by links that
// have already decided the days (the 3-day itinerary page, shared plans, a
// restored saved plan). Unlike generatePlan, both the day assignment AND the
// order of the stops are kept exactly as given — never re-clustered.
export function planFromDays(dayIdGroups, month) {
  return dayIdGroups.map((ids, i) => {
    const items = (Array.isArray(ids) ? ids : [])
      .map((id) => spotById(id))
      .filter(Boolean);
    const dayNum = i + 1;
    return rebuildDayOrder(
      items,
      // these are premade/curated arrangements (itinerary pages, the suggested
      // itinerary base, shared decided-day links) — so they keep their skip hints
      { day: dayNum, uid: items.length ? `d${dayNum}-${items[0].id}` : `empty-${dayNum}`, curated: true },
      month
    );
  });
}

// Restore the visitor's own auto-saved plan. Day assignments are kept exactly
// as saved (never re-clustered — the visitor chose which stops belong
// together), but each day's walking order is re-optimised: a stale order
// saved before an ordering fix would otherwise keep zig-zagging across the
// city forever. Shared links (/plan/:code, ?trip=) still keep their exact
// order via planFromDays — those are curated arrangements.
export function restoreSavedPlan(dayIdGroups, month) {
  return dayIdGroups.map((ids, i) => {
    let items = (Array.isArray(ids) ? ids : [])
      .map((id) => spotById(id))
      .filter(Boolean);
    // a smørrebrød bite on a Torvehallerne day stays inside the market,
    // matching what generatePlan would have produced
    if (items.some((s) => s.id === "torvehallerne") && items.some((s) => s.id === "smorrebrod"))
      items = items.map((s) =>
        s.id === "smorrebrod"
          ? { ...s, dur: 0.25, durLabel: "~15 min", pinned: true, note: "at Hallernes Smørrebrød in Torvehallerne", coords: SPOTS.find((x) => x.id === "torvehallerne").coords }
          : s
      );
    return buildDayFromItems(items, i + 1, month);
  });
}

// Tivoli is the one stop that floats: central, open late, and happiest as a
// day's evening cap. If its day gets packed past ~9h BY OTHER INDRE BY STOPS
// — usually extra picks joining the curated Day 2 — Tivoli yields the daytime
// and moves to cap the lightest day with no Indre By stops of its own (so it
// can never ping-pong back). Under 9h, or as the evening guest on another
// district's day, it stays put.
export const floatTivoli = (plan, month = "any") => {
  const fromIdx = plan.findIndex((d) => d.items.some((s) => s.id === "tivoli"));
  if (fromIdx < 0) return plan;
  const tivoli = spotById("tivoli");
  if (!tivoli) return plan;
  const from = plan[fromIdx];
  const hasIndreByPeers = from.items.some((s) => s.id !== "tivoli" && s.cluster === "indre-by");
  if (from.hours <= 9 || !hasIndreByPeers) return plan;
  let toIdx = -1;
  plan.forEach((d, i) => {
    if (i === fromIdx || d.hours > 8.5) return;
    if (d.items.some((x) => x.cluster === "indre-by")) return;
    if (toIdx < 0 || d.hours < plan[toIdx].hours) toIdx = i;
  });
  if (toIdx < 0) return plan;
  return plan.map((d, i) => {
    if (i === fromIdx) return rebuildDayOrder(d.items.filter((s) => s.id !== "tivoli"), d, month);
    if (i === toIdx) return rebuildDayOrder([...d.items, tivoli], d, month);
    return d;
  });
};

// Choose a home day for a new stop on an already-built plan. Keep an area
// together on its own district's day, but spread across the days you have:
// a spare/empty day gets filled before any single day is stacked to a
// punishing 11–12h. Only when there's genuinely no room does the district's
// day take it a bit over (an overpacked Indre By day can still float Tivoli off).
const INSERT_FULL = DAY_MAX + 1; // ~9h — a full but reasonable day ceiling
export const pickInsertDay = (plan, spot, allowed = () => true) => {
  const idxs = plan.map((_, i) => i).filter((i) => allowed(plan[i]));
  if (!idxs.length) return -1;
  const dur = spot.dur;
  const H = (i) => plan[i].hours;
  const hasArea = (i) => plan[i].items.some((x) => x.cluster === spot.cluster);
  const hasNear = (i) => plan[i].items.some((x) => AREA_AFFINITY.has(areaKey(x.cluster, spot.cluster)));
  const lightest = Math.min(...idxs.map(H));
  // A day is "comfortable" for one more stop when it stays under the ~9h
  // ceiling AND doesn't pull more than ~3h ahead of the lightest day we could
  // use instead — so spare days get filled rather than one day ballooning.
  const SPREAD = 3;
  const comfy = (i) => H(i) + dur <= INSERT_FULL && H(i) + dur <= Math.max(lightest + SPREAD, dur + 0.5);

  // 1) its own district, kept together while the day stays comfortable
  const same = idxs.filter((i) => hasArea(i) && comfy(i)).sort((a, b) => H(a) - H(b));
  if (same.length) return same[0];
  // 2) a spare/empty day beats overstuffing any single day
  const spare = idxs.filter((i) => plan[i].items.length === 0);
  if (spare.length) return spare[0];
  // 3) a neighbouring district with room
  const near = idxs.filter((i) => hasNear(i) && H(i) + dur <= DAY_MAX).sort((a, b) => H(a) - H(b));
  if (near.length) return near[0];
  // 4) no spare day — rejoin the district even a bit over (Tivoli can float off)
  const over = idxs.filter((i) => hasArea(i) && H(i) + dur <= 12).sort((a, b) => H(a) - H(b));
  if (over.length) return over[0];
  // 5) the lightest allowed day that can take it
  return idxs.reduce((b, i) => (b < 0 || H(i) < H(b) ? i : b), -1);
};



// Extend a curated-itinerary selection WITHOUT breaking it. When the visitor
// loaded the suggested itinerary and presses Build with extra picks and/or
// extra days: the suggested days keep their exact membership and order,
// extra picks join a curated day only when that day already covers their
// area (same district first, neighbouring district as fallback) and has room,
// and anything left over is clustered into the additional day(s). Tivoli may
// still float off an overpacked day.
export function extendCuratedPlan(selectedIds, days, month) {
  const curatedIds = new Set(ITINERARY_ALL.flat());
  const curated = ITINERARY_ALL.map((dayIds) => dayIds.filter((id) => selectedIds.includes(id))).filter((d) => d.length);
  const extras = selectedIds.filter((id) => !curatedIds.has(id));

  // the locked base — suggested days exactly as designed
  let plan = planFromDays(curated, month);

  // slot a stop into a day via the shared insertion rules (nearest neighbour,
  // never inside an always-adjacent pair, GoBoat closes a Brygge-less day) —
  // order otherwise kept
  const slotIn = (d, spot) => {
    const items = d.items.slice();
    items.splice(insertionIndexFor(items, spot), 0, spot);
    return rebuildDayOrder(items, d, month);
  };
  const putIn = (idx, spot) => {
    plan = plan.map((d, i) => (i === idx ? slotIn(d, spot) : d));
  };
  const sameArea = (d, spot, strict) =>
    d.items.some((x) =>
      strict ? x.cluster === spot.cluster : AREA_AFFINITY.has(areaKey(x.cluster, spot.cluster))
    );
  // museum fatigue: an extra museum never joins a day that already has 3
  const museumRoom = (d, s) => !isMuseum(s) || museumCount(d.items) < 3;

  // extra picks join a curated day if it already covers their area — the same
  // district even a bit over capacity (Tivoli can float off), a neighbouring
  // district only when it has room
  const rest = [];
  extras.forEach((id) => {
    const spot = spotById(id);
    if (!spot) return;
    let t = plan.findIndex((d) => d.hours + spot.dur <= 12 && sameArea(d, spot, true) && museumRoom(d, spot));
    if (t < 0) t = plan.findIndex((d) => d.hours + spot.dur <= DAY_MAX && sameArea(d, spot, false) && museumRoom(d, spot));
    if (t >= 0) putIn(t, spot);
    else rest.push(id);
  });

  // everything left over is clustered into the additional day(s)
  if (rest.length) {
    const spare = days - plan.length;
    if (spare > 0) {
      plan = plan.concat(generatePlan(rest, spare, month).filter((d) => d.items.length));
    } else {
      // no spare day — a coherent over-capacity day beats a wrong-area one:
      // same district, then neighbouring, then simply the roomiest
      rest.forEach((id) => {
        const spot = spotById(id);
        if (!spot) return;
        let t = plan.findIndex((d) => sameArea(d, spot, true) && museumRoom(d, spot));
        if (t < 0) t = plan.findIndex((d) => sameArea(d, spot, false) && museumRoom(d, spot));
        if (t < 0) t = plan.reduce((b, d, i) => (d.hours < plan[b].hours ? i : b), 0);
        putIn(t, spot);
      });
    }
  }

  // keep the chosen day count honest — pad with empty days to fill into
  while (plan.length < days) plan = plan.concat([buildDayFromItems([], plan.length + 1, month)]);
  plan = plan.map((d, i) => ({ ...d, day: i + 1 }));
  return floatTivoli(plan, month);
}

// Build a realistic plan: nearby spots share a day, big anchors get their own
// lighter day, day trips stand alone, and each day is ordered as a sensible walk.
// Build the indivisible units (coupled/pinned spots grouped) from a selection —
// shared by the standard area-merging packer and the balanced fallback packer.
function buildUnits(spotIds) {
  const chosen = spotIds.map((id) => spotById(id)).filter(Boolean);
  if (!chosen.length) return { units: [], pairs: [] };

  const bothMarket =
    chosen.some((s) => s.id === "torvehallerne") && chosen.some((s) => s.id === "smorrebrod");
  const bothTivoli =
    chosen.some((s) => s.id === "tivoli") && chosen.some((s) => s.id === "gasolinegrill");
  // Gasoline Grill also has a stall at Broens Gadekøkken — but Tivoli takes priority if both are picked.
  const bothBroens =
    !bothTivoli &&
    chosen.some((s) => s.id === "broens") && chosen.some((s) => s.id === "gasolinegrill");
  // Diamond Slice sits on Blågårdsgade — grab a slice during the stroll (1h total).
  const bothBlagards =
    chosen.some((s) => s.id === "blaagaards") && chosen.some((s) => s.id === "diamondpizza");
  // Ismageriet has a stall right by Kødbyen — treat the two as a short ~10 min hop.
  const bothKodbyen =
    chosen.some((s) => s.id === "ismageriet") && chosen.some((s) => s.id === "koedbyen");
  // Sandkaj swim sits right in Nordhavn — do both together, ~2¾h total (Nordhavn ~1h).
  const bothNordhavn =
    chosen.some((s) => s.id === "nordhavn") && chosen.some((s) => s.id === "swim-sandkaj");
  const coordOf = (id) => spotById(id).coords;

  const adjusted = chosen.map((s) => {
    // a smørrebrød bite inside Torvehallerne — pin it to the market so they stay together
    if (bothMarket && s.id === "smorrebrod")
      return { ...s, dur: 0.25, durLabel: "~15 min", pinned: true, note: "at Hallernes Smørrebrød in Torvehallerne", coords: coordOf("torvehallerne") };
    // a Gasoline Grill inside Tivoli — pin it to Tivoli
    if (bothTivoli && s.id === "gasolinegrill")
      return { ...s, dur: 0.5, durLabel: "~30 min", order: 3.9, note: "there's a Gasoline Grill inside Tivoli", coords: coordOf("tivoli") };
    // a Gasoline Grill at Broens Gadekøkken — pin it to Broens
    if (bothBroens && s.id === "gasolinegrill")
      return { ...s, dur: 0.5, durLabel: "~30 min", order: 2.1, note: "there's a Gasoline Grill at Broens Gadekøkken", coords: coordOf("broens") };
    // Diamond Slice is on Blågårdsgade — fold it into the stroll so the pair is ~1h total
    if (bothBlagards && s.id === "blaagaards") return { ...s, dur: 0.75 };
    if (bothBlagards && s.id === "diamondpizza")
      return { ...s, dur: 0.25, durLabel: "~15 min", note: "grab a slice at Diamond Slice on Blågårdsgade", coords: coordOf("blaagaards") };
    // pin Ismageriet to its Kødbyen stall so the day stays in Vesterbro —
    // it then IS the Kødbyen listing, so drop the duplicate entry and label
    // it Vesterbro (otherwise the same ice cream appears twice on the day)
    if (bothKodbyen && s.id === "ismageriet")
      return { ...s, order: 3.85, cluster: "vesterbro", note: "there's an Ismageriet stall by Kødbyen", coords: coordOf("ismageriet-kodbyen") };
    // Sandkaj swim is in Nordhavn — trim to ~2¾h together (Nordhavn ~1h, swim ~1¾h)
    if (bothNordhavn && s.id === "nordhavn") return { ...s, dur: 1 };
    if (bothNordhavn && s.id === "swim-sandkaj") return { ...s, dur: 1.75 };
    return s;
  }).filter((s) => !(bothKodbyen && s.id === "ismageriet-kodbyen"));

  // group inseparable spots into single units; everything else is its own unit
  const byId = new Map(adjusted.map((s) => [s.id, s]));
  const used = new Set();
  const units = [];
  const unitOf = new Map();
  // link two spots into one unit — extends an existing unit when one side is
  // already grouped (e.g. Sankt Peders joining the Torvehallerne+smørrebrød unit)
  const link = (x, y) => {
    if (!byId.has(x) || !byId.has(y)) return;
    const ux = unitOf.get(x);
    const uy = unitOf.get(y);
    if (ux && uy) return; // both already grouped
    if (ux) {
      ux.push(byId.get(y));
      unitOf.set(y, ux);
      used.add(y);
      return;
    }
    if (uy) {
      uy.push(byId.get(x));
      unitOf.set(x, uy);
      used.add(x);
      return;
    }
    const u = [byId.get(x), byId.get(y)];
    units.push(u);
    unitOf.set(x, u);
    unitOf.set(y, u);
    used.add(x);
    used.add(y);
  };
  const pairs = [];
  if (bothMarket) pairs.push(["torvehallerne", "smorrebrod"]);
  if (bothTivoli) pairs.push(["tivoli", "gasolinegrill"]);
  if (bothBroens) pairs.push(["broens", "gasolinegrill"]);
  if (bothBlagards) pairs.push(["blaagaards", "diamondpizza"]);
  if (bothKodbyen) pairs.push(["koedbyen", "ismageriet"]);
  if (bothNordhavn) pairs.push(["nordhavn", "swim-sandkaj"]);
  pairs.forEach(([x, y]) => link(x, y));
  // area-bound companions (bakeries, ice cream) join their area's unit so they
  // can never be split onto another day
  COUPLED.forEach(([x, y]) => {
    // Ismageriet belongs to Kødbyen when that pairing is on, not Østerbro
    if (y === "kartoffelraekkerne" && bothKodbyen) return;
    // Sankt Peders only joins the market unit on a smørrebrød market day —
    // otherwise Torvehallerne clusters with its true neighbours (Rosenborg,
    // Kongens Have, Rundetårn) instead of being dragged south
    if (x === "sanktpeders" && y === "torvehallerne" && !bothMarket) return;
    link(x, y);
  });
  adjusted.forEach((s) => {
    if (!used.has(s.id)) units.push([s]);
  });
  return { units, pairs };
}

// Day order shared by both packers: central days first (a natural
// centre-outward trip flow), day trips last.
const CORE_COORD = [55.6805, 12.585];
const sortPlanDays = (clusters) =>
  clusters.sort((a, b) => {
    if (!a.length) return 1;
    if (!b.length) return -1;
    const da = a.some(isDaytripSpot) ? 1 : 0;
    const db = b.some(isDaytripSpot) ? 1 : 0;
    if (da !== db) return da - db;
    return roadKmCoords(centroid(a), CORE_COORD) - roadKmCoords(centroid(b), CORE_COORD);
  });

// Fallback packer for very heavy selections: the merge-based packer can only
// merge, never split, so beyond ~60 spots it creates monster days. Balanced
// bin-packing instead — heaviest units first into the lightest day, with
// same-area/neighbouring days preferred. Day trips still stand alone.
function packBalanced(units, days) {
  const daytrips = units.filter((u) => u.some(isDaytripSpot));
  const rest = units.filter((u) => !u.some(isDaytripSpot));
  const plan = daytrips
    .sort((a, b) => spotsHours(b) - spotsHours(a))
    .slice(0, days)
    .map((u) => [...u]);
  while (plan.length < days) plan.push([]);
  const costTo = (u, day) => {
    if (!day.length) return 0.6; // slight nudge to fill empty days evenly
    const dayClusters = new Set(day.map((s) => s.cluster));
    let c = roadKmCoords(centroid(u), centroid(day));
    const shares = u.some((s) => dayClusters.has(s.cluster));
    const affinity = u.some((s) => [...dayClusters].some((x) => AREA_AFFINITY.has(areaKey(x, s.cluster))));
    if (!shares) c += affinity ? 0.5 : 3; // same penalty scale as mergeCost
    c += MUSEUM_PENALTY(museumCount(day) + museumCount(u));
    return c;
  };
  // the cardinal rule: an area stays on one day as best as possible — a unit
  // only ever lands on a day that already hosts its area (or a bonded
  // neighbour like Nørrebro↔Nordvest); foreign days are a genuine last resort
  const compatible = (u, day) =>
    !day.length ||
    u.some((s) =>
      day.some(
        (x) =>
          x.cluster === s.cluster ||
          AREA_AFFINITY.has(areaKey(x.cluster, s.cluster)) ||
          STRONG_BOND.has(areaKey(x.cluster, s.cluster))
      )
    );
  rest
    .sort((a, b) => spotsHours(b) - spotsHours(a))
    .forEach((u) => {
      const open = plan.map((_, i) => i).filter((i) => !plan[i].some(isDaytripSpot));
      const compat = open.filter((i) => compatible(u, plan[i]));
      const pool = compat.length ? compat : open;
      let best = -1;
      let bestScore = Infinity;
      pool.forEach((i) => {
        const score = spotsHours(plan[i]) + costTo(u, plan[i]) * 0.5; // hours dominate, geography breaks ties
        if (score < bestScore) {
          bestScore = score;
          best = i;
        }
      });
      if (best < 0) best = 0;
      plan[best] = plan[best].concat(u);
    });
  return plan;
}

// Final hour-leveling for extreme selections (the balanced fallback). Packing
// keeps areas coherent, so a few days can land just past the 8.5h line while
// another day has hours spare. Move the lightest movable stop (coupled
// partners ride along) off any day past the line onto the lightest day that
// stays under it — without ever breaking the same-area rule.
function levelDays(clusters, pairs, month, limit = 8.5) {
  const pairMap = new Map();
  [...COUPLED, ...pairs].forEach(([x, y]) => {
    pairMap.set(x, [...(pairMap.get(x) || []), y]);
    pairMap.set(y, [...(pairMap.get(y) || []), x]);
  });
  const LIMIT = limit;
  // judge days on the same ordered, travel-inclusive hours the final fit check
  // uses — raw concat-order hours overstate travel and block valid moves
  const H = (c) => (c.length ? buildDayFromItems(c, 0, month).hours : 0);
  const movable = (s) => !isDaytripSpot(s) && !isAnchorSpot(s) && !s.pinned && !AREA_BOUND.has(s.id);
  const stuck = new Set();
  let guard = 0;
  while (guard++ < 200) {
    let hi = -1;
    let hiH = -Infinity;
    clusters.forEach((c, i) => {
      if (stuck.has(i)) return;
      const h = H(c);
      if (h > LIMIT && c.some(movable) && h > hiH) {
        hiH = h;
        hi = i;
      }
    });
    if (hi < 0) break;
    // candidate moves, lightest first (stop + its coupled partners on this day)
    const cands = [];
    clusters[hi].forEach((s) => {
      if (!movable(s)) return;
      const ids = new Set([s.id]);
      (pairMap.get(s.id) || []).forEach((p) => {
        if (clusters[hi].some((x) => x.id === p)) ids.add(p);
      });
      const h = clusters[hi].filter((x) => ids.has(x.id)).reduce((t, x) => t + x.dur, 0);
      cands.push({ ids, h });
    });
    cands.sort((a, b) => a.h - b.h);
    // receivers keep the cardinal same-area rule: a stop only joins a day that
    // already hosts its area (or a bonded neighbour like Nørrebro↔Nordvest) —
    // never a foreign day, never a day-trip day
    const sameArea = (s, day) =>
      !day.length ||
      day.some(
        (x) =>
          x.cluster === s.cluster ||
          AREA_AFFINITY.has(areaKey(x.cluster, s.cluster)) ||
          STRONG_BOND.has(areaKey(x.cluster, s.cluster))
      );
    // Only apply a move that strictly lowers the trip's total overload —
    // first-fit without this check oscillates (A→B then B→A) at the margins.
    const over0 = clusters.reduce((t, c) => t + Math.max(0, H(c) - LIMIT), 0);
    let moved = false;
    for (const cand of cands) {
      if (moved) break;
      const moving = clusters[hi].filter((x) => cand.ids.has(x.id));
      const newHi = clusters[hi].filter((x) => !cand.ids.has(x.id));
      const targets = clusters
        .map((c, i) => i)
        .filter((i) => i !== hi && !clusters[i].some(isDaytripSpot) && moving.every((s) => sameArea(s, clusters[i])))
        .sort((a, b) => H(clusters[a]) - H(clusters[b]));
      for (const ti of targets) {
        const newT = clusters[ti].concat(moving);
        if (H(newT) > LIMIT) continue;
        if (museumCount(newT) > 3) continue; // museum fatigue cap
        const over1 =
          over0 -
          Math.max(0, H(clusters[hi]) - LIMIT) -
          Math.max(0, H(clusters[ti]) - LIMIT) +
          Math.max(0, H(newHi) - LIMIT) +
          Math.max(0, H(newT) - LIMIT);
        if (over1 < over0 - 1e-9) {
          clusters[hi] = newHi;
          clusters[ti] = newT;
          moved = true;
          break;
        }
      }
    }
    // the landscape changed on a successful move — parked days may have options
    // again (same retry rule as rebalanceDays)
    if (moved) stuck.clear();
    else stuck.add(hi);
  }
  return clusters;
}

// A stop is a STRAY when nothing else on its day shares its area or a bonded
// neighbour area (Jægersborggade alone on a Reffen day). The cardinal rule:
// activities in the same area stay on the same day as best as possible — a
// packed same-area day always beats a foreign placement, so strays move home
// to their area's host day regardless of the hour cap.
function rescueStrays(clusters, pairs) {
  const pairMap = new Map();
  [...COUPLED, ...pairs].forEach(([x, y]) => {
    pairMap.set(x, [...(pairMap.get(x) || []), y]);
    pairMap.set(y, [...(pairMap.get(y) || []), x]);
  });
  const bond = (a, b) => a === b || AREA_AFFINITY.has(areaKey(a, b)) || STRONG_BOND.has(areaKey(a, b));
  let guard = 0;
  let moved = true;
  while (moved && guard++ < 40) {
    moved = false;
    for (let di = 0; di < clusters.length && !moved; di++) {
      const day = clusters[di];
      for (const s of day) {
        if (s.pinned || s.isAd || isDaytripSpot(s) || isAnchorSpot(s)) continue;
        if (day.some((x) => x !== s && bond(x.cluster, s.cluster))) continue; // has a friend
        // home = the other day hosting the most of this stop's area
        let home = -1;
        let homeN = 0;
        clusters.forEach((c, i) => {
          if (i === di || c.some(isDaytripSpot)) return;
          const n = c.filter((x) => x.cluster === s.cluster).length;
          if (n > homeN) {
            homeN = n;
            home = i;
          }
        });
        if (home < 0) continue; // no day hosts its area — already best effort
        const ids = new Set([s.id]);
        (pairMap.get(s.id) || []).forEach((p) => {
          if (day.some((x) => x.id === p)) ids.add(p);
        });
        const moving = day.filter((x) => ids.has(x.id));
        clusters[di] = day.filter((x) => !ids.has(x.id));
        clusters[home] = clusters[home].concat(moving);
        moved = true;
        break;
      }
    }
  }
  return clusters;
}

export function generateBalancedPlan(spotIds, days, month, limit = 8.5) {
  const { units, pairs } = buildUnits(spotIds);
  if (!units.length) return Array.from({ length: days }, (_, i) => buildDayFromItems([], i + 1, month));
  const clusters = levelDays(
    rescueStrays(reuniteAreas(rebalanceDays(packBalanced(units, days), pairs)), pairs),
    pairs,
    month,
    limit
  );
  const plan = sortPlanDays(clusters).map((items, i) => buildDayFromItems(items, i + 1, month));
  return floatTivoli(plan, month);
}

// Build a realistic plan: nearby spots share a day, big anchors get their own
// lighter day, day trips stand alone, and each day is ordered as a sensible walk.
export function generatePlan(spotIds, days, month) {
  const { units, pairs } = buildUnits(spotIds);
  if (!units.length) return Array.from({ length: days }, (_, i) => buildDayFromItems([], i + 1, month));

  const clusters = rescueStrays(clusterDays(units, days, pairs), pairs);
  const plan = clusters.map((items, i) => buildDayFromItems(items, i + 1, month));
  // a packed Tivoli day (usually the curated Day 2 plus extra Indre By picks)
  // hands Tivoli to the lightest day — it's central, open late, and the
  // perfect evening cap anywhere
  return floatTivoli(plan, month);
}

// --- Bridge-aware travel model -------------------------------------------
// Copenhagen's harbour splits the city into a "city" side and an "amager" side
// (Christianshavn / Islands Brygge / Refshaleøen). You can only cross on a
// bridge, and Refshaleøen is a long dead-end reached via Refshalevej — so a
// straight line badly underestimates those legs. We route via real crossings.
const R_EARTH = 6371; // km
const toRad = (d) => (d * Math.PI) / 180;
const havKm = (a, b) => {
  const dLat = toRad(b[0] - a[0]);
  const dLng = toRad(b[1] - a[1]);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLng / 2) ** 2;
  return 2 * R_EARTH * Math.asin(Math.sqrt(h));
};

const BRIDGES = [
  { name: "the Butterfly bridge", coords: [55.679, 12.5974] }, // the 3-way bridge: Opera Park ↔ Papirøen ↔ Broens quay
  { name: "Inderhavnsbroen", coords: [55.6772, 12.596] },
  { name: "Knippelsbro", coords: [55.674, 12.586] },
  { name: "Langebro", coords: [55.6668, 12.576] },
  { name: "Lille Langebro", coords: [55.669, 12.5772] },
  { name: "Bryggebroen", coords: [55.6605, 12.5668] },
];
const REFSHALE_NECK = [55.6835, 12.6015]; // where Refshalevej leaves Holmen

const AMAGER_CLUSTERS = new Set(["christianshavn", "islands-brygge", "refshaleoen"]);
const sideOf = (s) => {
  if (s.cluster === "daytrip") return "daytrip";
  if (s.id === "broens") return "amager"; // Broens is on the Christianshavn quay
  return AMAGER_CLUSTERS.has(s.cluster) ? "amager" : "city";
};

// Realistic door-to-door distance, detouring via the nearest bridge / the
// Refshaleøen neck when a leg has to cross water.
function roadRoute(a, b) {
  const STREET = 1.4; // streets aren't perfectly straight
  const aRef = a.cluster === "refshaleoen";
  const bRef = b.cluster === "refshaleoen";

  // Both stops are out on Refshaleøen → same island, so it's just the direct
  // walk between them. Don't route each one via the neck (that double-counts
  // the detour and turns a 150 m hop into a fake 35-min trek).
  if (aRef && bRef) {
    return { km: havKm(a.coords, b.coords) * STREET, crossing: null, refshale: true };
  }

  let extra = 0;
  let pa = a.coords;
  let pb = b.coords;
  let refshale = false;
  if (aRef) {
    extra += havKm(a.coords, REFSHALE_NECK);
    pa = REFSHALE_NECK;
    refshale = true;
  }
  if (bRef) {
    extra += havKm(b.coords, REFSHALE_NECK);
    pb = REFSHALE_NECK;
    refshale = true;
  }
  const esa = a.cluster === "refshaleoen" ? "amager" : sideOf(a);
  const esb = b.cluster === "refshaleoen" ? "amager" : sideOf(b);
  let crossing = null;
  let baseKm;
  if (esa === esb) {
    baseKm = havKm(pa, pb);
  } else {
    let best = Infinity;
    BRIDGES.forEach((br) => {
      const d = havKm(pa, br.coords) + havKm(br.coords, pb);
      if (d < best) {
        best = d;
        crossing = br.name;
      }
    });
    baseKm = best;
  }
  return { km: (extra + baseKm) * STREET, crossing, refshale };
}

// Which side of the harbour a raw coordinate sits on (no cluster needed).
// The inner-harbour channel runs NNW–SSE; points east of it are "amager".
const sideByCoord = (c) => {
  const boundaryLon = 12.577 + (c[0] - 55.665) * 1.067;
  return c[1] > boundaryLon ? "amager" : "city";
};
const isRefshaleCoord = (c) => c[1] > 12.603 && c[0] > 55.681 && c[0] < 55.697;

// Coordinate-based version of roadRoute — used for day clustering & ordering so
// two stops across the water are correctly "far" even if close as the crow flies.
function roadKmCoords(A, B, street = 1.4) {
  const STREET = street;
  // Both out on Refshaleøen → direct distance, no neck double-count.
  if (isRefshaleCoord(A) && isRefshaleCoord(B)) {
    return havKm(A, B) * STREET;
  }
  let extra = 0;
  let pa = A;
  let pb = B;
  if (isRefshaleCoord(A)) {
    extra += havKm(A, REFSHALE_NECK);
    pa = REFSHALE_NECK;
  }
  if (isRefshaleCoord(B)) {
    extra += havKm(B, REFSHALE_NECK);
    pb = REFSHALE_NECK;
  }
  let base;
  if (sideByCoord(pa) === sideByCoord(pb)) {
    base = havKm(pa, pb);
  } else {
    base = Math.min(...BRIDGES.map((br) => havKm(pa, br.coords) + havKm(br.coords, pb)));
  }
  return (extra + base) * STREET;
}

// Public-transport times for day trips (door-to-door from central Copenhagen).
const DAYTRIP_TRANSIT = {
  bakken: { min: 30, label: "by S-train", note: "Line C to Klampenborg + ~10 min walk through Dyrehaven" },
  louisiana: { min: 45, label: "by train", note: "Coast line to Humlebæk + ~10 min walk" },
  helsingor: { min: 50, label: "by train", note: "Coast line to Helsingør — Kronborg is a 15-minute walk from the station" },
  malmo: { min: 40, label: "by train", note: "Øresund train to Malmö C — bring your passport" },
  dragor: { min: 40, label: "by bus", note: "Bus 250S or 35 from the city + a short walk" },
  roskilde: { min: 30, label: "by train", note: "Regional train to Roskilde — cathedral 10 min from the station" },
};

export const dayTripTransit = (s) => (s.cluster === "daytrip" ? DAYTRIP_TRANSIT[s.id] || null : null);

// Rough door-to-door leg between two consecutive stops, walking-first with
// Metro/bus for longer hops and train/bus for day trips.
const NORDHAVN_STOPS = new Set(["nordhavn", "swim-sandkaj"]);
const CENTRE_CLUSTERS = new Set(["indre-by", "harbour"]);
// The clustering/ordering heuristics (and the walk-vs-transit decision below)
// use a conservative 1.4 street-detour factor so stops across water read as
// genuinely "far". But the distance AND time we show the visitor use a gentler
// ~1.05 factor: Copenhagen's centre is walked along direct harbour promenades
// and pedestrian streets, so the real on-foot figures land close to Google Maps
// rather than ~40% above. WALK_SCALE converts a 1.4-based km into that 1.05 one.
const WALK_DISPLAY_STREET = 1.05;
const WALK_SCALE = WALK_DISPLAY_STREET / 1.4;
export function travelBetween(a, b) {
  const key = [a.id, b.id].sort().join("|");
  if (key === "ismageriet|koedbyen") return { mode: "walk", min: 10, label: "on foot" };
  // Curated day 4: skip the 25-min walk down to Vesterbro — the Metro from
  // Gammel Strand lands you at the top of Istedgade in minutes.
  if (key === "christiansborg|istedgade")
    return { mode: "transit", min: 12, label: "by Metro", note: "M3/M4 between Gammel Strand and København H — Istedgade starts at the station" };
  // Curated day 4 closer: an easy half-hour walk, or one S-train stop.
  if (key === "carlsberg|koedbyen")
    return { mode: "walk", min: 30, label: "on foot", note: "or hop the S-train one stop between Carlsberg and Dybbølsbro stations" };
  if (key === "carlsbergbyen|koedbyen")
    return { mode: "walk", min: 20, label: "on foot", note: "or hop the S-train one stop: Carlsberg → Dybbølsbro" };
  // Curated day 5: both sit on the same coast line — a short hop, not a ride
  // back to the city first. (Louisiana always comes first: the line from the
  // city passes Humlebæk before Helsingør.)
  if (key === "helsingor|louisiana")
    return a.id === "helsingor"
      ? { mode: "transit", min: 35, label: "by train", note: "back to Helsingør station, then the coast line to Humlebæk + ~10 min walk" }
      : { mode: "transit", min: 35, label: "by train", note: "back to Humlebæk station, then the coast line to Helsingør — Kronborg is a 15-min walk from the station" };
  // Bispebjerg (NW) ↔ the Zoo (Frederiksberg, W): close as the crow flies, but
  // there's no cross-town line — you route via the city, so it's really ~45 min.
  if (key === "bispebjerg|zoo") return { mode: "transit", min: 45, label: "by Metro or bus" };
  // Bispebjerg ↔ Superkilen: no train or metro up here — bus 6A through Nordvest
  // (~26 min), or a 45-min walk.
  if (key === "bispebjerg|superkilen")
    return { mode: "transit", min: 26, label: "by bus", note: "bus 6A through Nordvest — or a ~45-min walk" };
  // Nordhavn (Orientkaj) ↔ the city centre by harbour bus: the 991/992 boats
  // meander down the harbour — scenic but slow, about 40 min — so the planner
  // doesn't under-count the ride back after a Sandkaj swim.
  if (
    (NORDHAVN_STOPS.has(a.id) && CENTRE_CLUSTERS.has(b.cluster)) ||
    (NORDHAVN_STOPS.has(b.id) && CENTRE_CLUSTERS.has(a.cluster))
  )
    return { mode: "transit", min: 40, label: "by harbour bus", note: "harbour bus 991/992 down the harbour from Orientkaj — scenic but slow" };
  if (a.cluster === "daytrip" || b.cluster === "daytrip") {
    const trip = a.cluster === "daytrip" ? a : b;
    const t = DAYTRIP_TRANSIT[trip.id];
    return t
      ? { mode: "transit", min: t.min, label: t.label, note: t.note }
      : { mode: "transit", min: 45, label: "by train" };
  }
  const { km, crossing, refshale } = roadRoute(a, b);
  if (km < 0.12) return null; // essentially the same place
  const round5 = (m) => Math.round(m / 5) * 5;
  // Nordvest (Grundtvigs, Bispebjerg) sits off the fast lines — any hop between
  // it and the rest of the city realistically takes at least 30 min door-to-door.
  const nordvestLeg = (a.cluster === "nordvest") !== (b.cluster === "nordvest");
  if (km <= 3.0 && !nordvestLeg) {
    const note = crossing ? `via ${crossing}` : refshale ? "along Refshalevej" : null;
    // Minutes reflect the gentler, shown walking distance (km is 1.4-based; scale
    // it to the ~1.05 figure) so "X min on foot" matches the "km covered" total.
    return { mode: "walk", min: Math.max(5, round5(((km * WALK_SCALE) / 4.8) * 60)), label: "on foot", note };
  }
  let min = Math.max(10, round5(9 + (km / 22) * 60));
  if (nordvestLeg) min = Math.max(min, 30);
  const label = refshale ? "by bus 2A / harbour bus" : nordvestLeg ? "by bus" : "by Metro or bus";
  // Don't name a specific bridge on transit legs — the geometrically nearest
  // crossing is often pedestrian-only (e.g. Inderhavnsbroen), which buses can't
  // use. Keep only the genuinely useful Refshaleøen hint.
  const note = refshale ? "up Refshalevej" : null;
  return { mode: "transit", min, label, note };
}

export const spotDur = (s) => (s.durLabel ? s.durLabel : "~" + fmtHours(s.dur));

// Day hours = time at the spots + the travel legs between consecutive stops,
// so the estimate reflects the real day, not just the time on location.
const dayHoursWithTravel = (items) => {
  let travelMin = 0;
  for (let i = 1; i < items.length; i++) {
    const leg = travelBetween(items[i - 1], items[i]);
    if (leg) travelMin += leg.min;
  }
  return Math.round((spotsHours(items) + travelMin / 60) * 100) / 100;
};

// Per-day walking totals for the day card: minutes on foot and ground covered.
// Walking legs only — Metro/train/bus hops aren't walking. Uses the gentler
// WALK_DISPLAY_STREET factor (see note by travelBetween) so the shown km tracks
// Google Maps rather than the 1.4 detour factor the routing heuristics use.
export const walkTotals = (items) => {
  let min = 0;
  let km = 0;
  for (let i = 1; i < items.length; i++) {
    const leg = travelBetween(items[i - 1], items[i]);
    if (leg && leg.mode === "walk") {
      min += leg.min;
      km += roadKmCoords(items[i - 1].coords, items[i].coords, WALK_DISPLAY_STREET);
    }
  }
  return { min, km };
};

export const fmtHours = (h) => {
  if (!h) return "0h";
  const q = Math.round(h * 4) / 4;
  const whole = Math.floor(q);
  const frac = q - whole;
  const sym = frac === 0.25 ? "¼" : frac === 0.5 ? "½" : frac === 0.75 ? "¾" : "";
  return `${whole > 0 ? whole : sym ? "" : 0}${sym}h`;
};

// Inject paid advertiser spots (tier 2+) into the planner's selectable list at
// runtime. They flow through the itinerary like any other spot (clustered by
// their geocoded coords). Idempotent — safe to call on every ads refresh.
const _registeredAdIds = new Set();
export function registerAdSpots(ads) {
  (ads || []).forEach((ad) => {
    const id = `ad_${ad.id}`;
    if (ad.tier < 2 || !ad.coords || _registeredAdIds.has(id)) return;
    _registeredAdIds.add(id);
    SPOTS.push({
      id,
      name: ad.business_name,
      cluster: "partners",
      dur: 1,
      seasons: ALL,
      order: 2.6,
      coords: ad.coords,
      isAd: true,
      note: ad.description ? ad.description.slice(0, 90) : null,
    });
  });
}

